import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import MicButton from "@/components/enea/MicButton";
import {
  Sparkles, Loader2, CheckCircle2, RotateCcw, Paperclip, FileText, X, ImagePlus,
  AlertTriangle, Download, Trash2, ShieldCheck, TrendingUp, Compass, Phone,
  CalendarClock, MapPin, Star, Info, Send, ClipboardList, Search, Flame,
} from "lucide-react";

export const GOALS = [
  { id: "scambio_casa", label: "Devo vendere questa casa per comprarne un'altra (voglio informazioni su Scambio Casa)", short: "Scambio Casa" },
  { id: "solo_vendere", label: "Devo solo vendere e monetizzare", short: "Vendita" },
  { id: "curioso", label: "Sono solo curioso, non voglio vendere ora", short: "Curioso" },
];

const RISK_OPTIONS = [
  { id: "certificati", label: "Voglio vendere solo ad acquirenti finanziariamente certificati, anche se significa fare una selezione rigida in partenza" },
  { id: "vincolate", label: "Sono disposto ad accettare proposte vincolate al mutuo, tenendo bloccata la mia casa per 4–6 mesi col rischio che la vendita salti" },
];

const SALE_INTENTS = [
  { id: "vendo_subito", label: "Voglio vendere SUBITO", hot: true },
  { id: "entro_3_mesi", label: "Voglio vendere entro 3 mesi", hot: true },
  { id: "entro_6_mesi", label: "Voglio vendere entro 6 mesi" },
  { id: "oltre_6_mesi", label: "Oltre 6 mesi, non ho fretta" },
  { id: "solo_informativa", label: "È solo un'informativa: per ora non vendo" },
];

const EXTERIOR_OPTIONS = [
  { id: "ottima", label: "Ottima — ritinteggiato entro 2 anni, cappotto installato ove necessario" },
  { id: "buona", label: "Buona — esterno del fabbricato rifatto interamente entro 6 anni" },
  { id: "media", label: "Media — si presenta bene, ma con parti da ritinteggiare" },
  { id: "scarsa", label: "Scarsa / pessima — necessita pitture e rinfresco delle pareti" },
];

const DIAG_STEPS = [
  "Incrocio dei dati con il compravenduto reale in zona…",
  "Calcolo del rischio di svalutazione…",
  "Verifica del bacino acquirenti certificati e pre-deliberati attivi…",
  "Generazione del report diagnostico completata.",
];

const PROPERTY_TYPES = [
  { id: "Appartamento", label: "Appartamento" },
  { id: "Attico", label: "Attico" },
  { id: "Monolocale", label: "Monolocale" },
  { id: "Loft / Open space", label: "Loft / Open space" },
  { id: "Casa a schiera", label: "Casa a schiera" },
  { id: "Villa / Casa indipendente", label: "Villa / Casa indipendente (bi-, tri-, quadrifamiliare)" },
  { id: "Garage / Posto auto", label: "Garage / Posto auto" },
  { id: "Altro", label: "Altro" },
];

const FLOORS = [
  { id: "terra", label: "Piano terra" },
  { id: "1", label: "1° piano" },
  { id: "2", label: "2° piano" },
  { id: "3plus", label: "3° piano o superiore" },
  { id: "attico", label: "Attico · ultimo piano" },
];

const STATO_CONSERVATIVO = [
  { id: "al_grezzo", label: "Al grezzo / inagibile" },
  { id: "da_ristrutturare", label: "Da ristrutturare completamente" },
  { id: "parzialmente_da_ristrutturare", label: "Parzialmente da ristrutturare (bagno/cucina, impianti)" },
  { id: "da_rinfrescare", label: "Da rinfrescare (tinteggiatura, piccoli lavori)" },
  { id: "buono", label: "Buono / abitabile" },
  { id: "ristrutturato", label: "Ristrutturato / ottime condizioni" },
  { id: "nuovo_recente", label: "Nuovo / recente (es. 2022)" },
  { id: "completamente_ristrutturato_mai_abitato", label: "Completamente ristrutturato, mai abitato" },
];

const ZONE_TIERS = [
  { id: "centro", label: "Centro — il cuore della città" },
  { id: "semicentro", label: "Semi-centro — a pochi minuti dal centro" },
  { id: "prima_periferia", label: "Prima periferia — residenziale ben collegata" },
  { id: "periferia", label: "Periferia — aree più esterne del comune" },
  { id: "extraurbano", label: "Extra-urbano — campagna o fuori dal centro abitato" },
];

const VALUATION_TYPES = [
  { id: "classica", label: "Valutazione Classica", price: "GRATUITA", desc: "Stima indicativa immediata basata sui dati inseriti e sulle quotazioni OMI ufficiali (range min–max reale della zona). Report PDF incluso." },
  { id: "certificata", label: "Valutazione Premium ENEA", price: "€279", desc: "Il valore preciso e difendibile: brochure di oltre 10 pagine che spiega, fattore per fattore, perché il tuo immobile vale quello che vale. Analisi dei 22 fattori Premium (incidono fino al 40% del valore), quotazioni OMI di zona con grafico, strategia di vendita e call di 30 minuti con un consulente Gruppo Vela (la prenoti tu online). Pagamento sicuro online con PayPal (conto PayPal o carta): la brochure arriva via email subito dopo il pagamento." },
];

const TERRAZZO_TOOLTIP = "Inserire solo i mq che sporgono oltre il filo del muro esterno dell'edificio";

const APE_OPTIONS = [
  { id: "a4a1", label: "A (A4–A1)" }, { id: "b", label: "B" }, { id: "c", label: "C" }, { id: "d", label: "D" },
  { id: "e", label: "E" }, { id: "f", label: "F" }, { id: "g", label: "G" }, { id: "nd", label: "Non disponibile" },
];
const HEATING_OPTIONS = [
  { id: "autonomo", label: "Autonomo" }, { id: "centralizzato", label: "Centralizzato" }, { id: "assente", label: "Assente" },
];
const WINDOWS_OPTIONS = [
  { id: "triplo", label: "Triplo vetro" }, { id: "doppio", label: "Doppio vetro" }, { id: "singolo", label: "Vetro singolo" },
];
const VIEW_OPTIONS = [
  { id: "panoramica", label: "Panoramica / aperta" }, { id: "cortile", label: "Cortile / verde interno" }, { id: "strada", label: "Strada trafficata" },
];
const OCCUPANCY_OPTIONS = [
  { id: "libero", label: "Libero subito" }, { id: "locato", label: "Locato / affittato" },
];
const MAX_FORM_PHOTOS = 10;
const CALL_SLOTS = ["09:00–11:00", "11:00–13:00", "14:00–16:00", "16:00–18:00"];
const DRAFT_KEY = "hec_val_draft";
const APT_LIKE = ["Appartamento", "Monolocale", "Loft / Open space", "Altro"];

function nextWorkingDays(n) {
  const days = [];
  const d = new Date();
  while (days.length < n) {
    d.setDate(d.getDate() + 1);
    const dow = d.getDay();
    if (dow !== 0 && dow !== 6) {
      const label = d.toLocaleDateString("it-IT", { weekday: "short", day: "numeric", month: "short" });
      days.push({ value: label, label: label.charAt(0).toUpperCase() + label.slice(1) });
    }
  }
  return days;
}

function parseNum(v) {
  if (v === null || v === undefined) return null;
  const n = parseFloat(String(v).replace(/[^\d.,-]/g, "").replace(",", "."));
  return Number.isFinite(n) ? n : null;
}

const fmtEur = (n) => (n === null || n === undefined) ? "—" : "€ " + new Intl.NumberFormat("it-IT").format(n);

const StarRating = ({ value, onChange, testId }) => (
  <div className="flex gap-1" data-testid={testId}>
    {[1, 2, 3, 4, 5].map((n) => (
      <button key={n} type="button" onClick={() => onChange(n)} data-testid={`${testId}-${n}`}
        className="p-0.5 transition-transform hover:scale-110">
        <Star className={`w-6 h-6 ${n <= value ? "fill-amber-400 text-amber-400" : "text-slate-300"}`} />
      </button>
    ))}
  </div>
);

const EMPTY_FORM = {
  nome: "", goal: "", rischio: "", intenzione: "", zona: "", tipologia: "", mq: "",
  piano: "", ascensore: "", ascensoreInCasa: "", garage: "", garageSqm: "",
  hasGarden: false, gardenSqm: "", hasTerrazzo: false, terrazzoSqm: "",
  gardenType: "", bonus110: "", espoPunti: [],
  stato: "", esterno: "", anno: "", note: "", tipoValutazione: "classica",
  usufrutto: false, usufruttoEta: "",
  locali: "", bagni: "", ape: "", riscaldamento: "", clima: "", infissi: "",
  esposizione: "", vista: "", speseCond: "", cantina: "", occupazione: "", annoRistr: "",
  email: "", telefono: "",
};
const EMPTY_ADDR = { via: "", civico: "", comune: "", provincia: "" };

const inputCls = "w-full px-3 py-2.5 rounded-lg border border-slate-200 bg-slate-50/70 text-sm text-[#0b2545] placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-[#14B8A6]/40 focus:border-[#14B8A6]";
const labelCls = "block text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-500 mb-1";

export default function ValuationFlow({ beta = false, initialName = "", initialPremium = false }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [addr, setAddr] = useState(EMPTY_ADDR);
  const [verified, setVerified] = useState(null);
  const [addrVerifying, setAddrVerifying] = useState(false);
  const [addrError, setAddrError] = useState("");
  const [addrFieldError, setAddrFieldError] = useState(null);
  const [addrSuggestions, setAddrSuggestions] = useState([]);
  const [searchQ, setSearchQ] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [offer, setOffer] = useState(null);
  const [fiscal, setFiscal] = useState({ name: "", address: "", taxCode: "" });
  const [fiscalErr, setFiscalErr] = useState({});
  const [visuraReq, setVisuraReq] = useState(false);
  const [formError, setFormError] = useState("");
  const [phase, setPhase] = useState("form"); // form | result | done
  const [computing, setComputing] = useState(false);
  const [valuation, setValuation] = useState(null);
  const [photoAnalysis, setPhotoAnalysis] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [paying, setPaying] = useState(false);
  const [leadId, setLeadId] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [docs, setDocs] = useState([]);
  const [uploadingDocs, setUploadingDocs] = useState(false);
  const [uploadingPhotos, setUploadingPhotos] = useState(false);
  const [draftRestored, setDraftRestored] = useState(false);
  const [callOpen, setCallOpen] = useState(false);
  const [callDate, setCallDate] = useState("");
  const [callSlot, setCallSlot] = useState("");
  const [callSubmitting, setCallSubmitting] = useState(false);
  const [callDone, setCallDone] = useState(false);
  const [upgradeDone, setUpgradeDone] = useState(null);
  const [upgradeSubmitting, setUpgradeSubmitting] = useState(false);
  const [easeRating, setEaseRating] = useState(0);
  const [trustRating, setTrustRating] = useState(0);
  const [feedbackText, setFeedbackText] = useState("");
  const [feedbackSubmitting, setFeedbackSubmitting] = useState(false);
  const [feedbackDone, setFeedbackDone] = useState(false);
  const docsInputRef = useRef(null);
  const photosInputRef = useRef(null);
  const restoredRef = useRef(false);
  const topRef = useRef(null);
  const submitIdRef = useRef(null);
  const ensureSubmitId = () => {
    if (!submitIdRef.current) {
      submitIdRef.current = window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    }
    return submitIdRef.current;
  };

  // Draft restore
  useEffect(() => {
    try {
      const raw = localStorage.getItem(DRAFT_KEY);
      if (raw) {
        const d = JSON.parse(raw);
        if (d && d.ts && Date.now() - d.ts < 7 * 24 * 3600 * 1000) {
          setForm({ ...EMPTY_FORM, ...(d.form || {}) });
          setAddr({ ...EMPTY_ADDR, ...(d.addr || {}) });
          if (d.verified) setVerified(d.verified);
          setDraftRestored(true);
        } else {
          localStorage.removeItem(DRAFT_KEY);
        }
      }
    } catch (e) { /* localStorage non disponibile */ }
    if (initialName) setForm((f) => ({ ...f, nome: initialName }));
    if (initialPremium) setForm((f) => ({ ...f, tipoValutazione: "certificata" }));
    restoredRef.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Draft autosave
  useEffect(() => {
    if (!restoredRef.current) return;
    try {
      if (phase === "done") { localStorage.removeItem(DRAFT_KEY); return; }
      const hasData = Object.entries(form).some(([k, v]) => v && v !== EMPTY_FORM[k]) || Object.values(addr).some(Boolean);
      if (hasData) localStorage.setItem(DRAFT_KEY, JSON.stringify({ form, addr, verified, ts: Date.now() }));
    } catch (e) { /* noop */ }
  }, [form, addr, verified, phase]);

  useEffect(() => {
    api.get("/premium-offer").then(({ data }) => setOffer(data)).catch(() => {});
  }, []);

  // Autocomplete indirizzo (Photon/OSM) con debounce
  useEffect(() => {
    const q = searchQ.trim();
    if (q.length < 3) { setSearchResults([]); setSearchOpen(false); return undefined; }
    const t = setTimeout(async () => {
      setSearchLoading(true);
      try {
        const { data } = await api.get("/address-suggest", { params: { q } });
        setSearchResults(data.suggestions || []);
        setSearchOpen((data.suggestions || []).length > 0);
      } catch (e) { /* noop */ } finally { setSearchLoading(false); }
    }, 350);
    return () => clearTimeout(t);
  }, [searchQ]);

  const setF = (patch) => { setForm((f) => ({ ...f, ...patch })); if (formError) setFormError(""); };
  const setA = (patch) => { setAddr((a) => ({ ...a, ...patch })); setVerified(null); if (addrError) setAddrError(""); setAddrFieldError(null); setAddrSuggestions([]); };

  const t = form.tipologia;
  const showFloor = APT_LIKE.includes(t);
  const showElevator = showFloor || t === "Attico";
  const showGarage = !!t && t !== "Garage / Posto auto";
  const showGardenCheckbox = t === "Appartamento";
  const showGardenField = (t === "Appartamento" && form.hasGarden) || t === "Casa a schiera" || t === "Villa / Casa indipendente";
  const showTerrazzoCheckbox = t === "Appartamento";
  const showTerrazzoField = (t === "Appartamento" && form.hasTerrazzo) || t === "Attico";

  const floorValue = showFloor ? (form.piano || null) : (t === "Attico" ? "attico" : null);
  const elevatorValue = showElevator && form.ascensore ? form.ascensore === "si" : null;
  const garageValue = showGarage && form.garage ? form.garage !== "no" : null;

  const launchActive = !!offer?.active;
  const premiumPrice = launchActive ? offer.price_eur : 279;
  const totalAmount = premiumPrice + (visuraReq ? 5 : 0);
  const addrInputCls = (field) => inputCls + (addrFieldError === field ? " !border-red-400 ring-2 ring-red-300/60 !bg-red-50" : "");

  const verifyAddress = async (override) => {
    if (addrVerifying) return;
    const a = override && override.via !== undefined ? override : addr;
    const via = (a.via || "").trim(), civico = (a.civico || "").trim(), comune = (a.comune || "").trim(), provincia = (a.provincia || "").trim();
    const labels = { provincia: "provincia", comune: "comune", via: "via", civico: "numero civico" };
    const missing = [!provincia && "provincia", !comune && "comune", !via && "via", !civico && "civico"].filter(Boolean);
    if (missing.length) {
      setAddrFieldError(missing[0]);
      setAddrSuggestions([]);
      setAddrError(missing.length === 1
        ? `Manca solo un campo: ${labels[missing[0]]}. Compila il campo evidenziato in rosso e premi di nuovo Verifica.`
        : `Mancano questi campi: ${missing.map((m) => labels[m]).join(", ")}. Compilali e premi di nuovo Verifica.`);
      return;
    }
    setAddrVerifying(true);
    setAddrError(""); setAddrFieldError(null); setAddrSuggestions([]);
    try {
      const { data: res } = await api.get("/address-verify", { params: { via, civico, comune, provincia }, timeout: 25000 });
      if (res?.verified) {
        setVerified({ label: res.label, city: res.city || comune, postcode: res.postcode || null, province: res.province || provincia, lat: res.lat, lon: res.lon, match: res.match });
        toast.success("Indirizzo verificato sulle mappe ✓");
      } else if (res?.error) {
        setAddrError("⚠️ Il servizio mappe è momentaneamente non disponibile (non dipende dal tuo indirizzo). Attendi qualche istante e premi di nuovo Verifica.");
      } else if (res?.error_field) {
        setAddrFieldError(res.error_field);
        setAddrError(res.message || "Indirizzo non trovato: controlla il campo evidenziato in rosso.");
        setAddrSuggestions(res.suggestions || []);
      } else {
        setAddrError("Indirizzo non trovato sulle mappe. Controlla che via, civico, comune e provincia siano corretti (es. Via Roma · 12 · Padova · PD) e riprova.");
      }
    } catch (e) {
      setAddrError("Verifica non riuscita in questo momento. Attendi qualche secondo e riprova.");
    } finally {
      setAddrVerifying(false);
    }
  };

  const pickSuggestion = (s) => {
    const next = { via: s.street || "", civico: s.housenumber || "", comune: s.city || "", provincia: s.province || "" };
    setAddr(next);
    setVerified(null);
    setSearchQ(""); setSearchResults([]); setSearchOpen(false);
    setAddrError(""); setAddrFieldError(null); setAddrSuggestions([]);
    if (next.via && next.civico && next.comune && next.provincia) {
      verifyAddress(next);
    } else {
      const miss = [!next.provincia && "provincia", !next.comune && "comune", !next.via && "via", !next.civico && "civico"].filter(Boolean);
      if (miss.length) {
        setAddrFieldError(miss[0]);
        setAddrError(`Quasi fatto! Completa a mano: ${miss.join(", ")} — poi premi Verifica indirizzo.`);
      }
    }
  };

  const applySuggestion = (s) => {
    const patch = addrFieldError === "comune" ? { comune: s } : addrFieldError === "via" ? { via: s } : { civico: s };
    const next = { ...addr, ...patch };
    setAddr(next);
    setVerified(null);
    setAddrError(""); setAddrFieldError(null); setAddrSuggestions([]);
    verifyAddress(next);
  };

  const handleDocs = async (fileList) => {
    if (!fileList || !fileList.length || uploadingDocs) return;
    const allowed = ["application/pdf", "image/jpeg", "image/png", "image/webp"];
    const files = Array.from(fileList).slice(0, 10).filter((f) => {
      if (!allowed.includes(f.type)) { toast.error(`Formato non supportato: ${f.name}`); return false; }
      if (f.size > 10 * 1024 * 1024) { toast.error(`File troppo grande: ${f.name}`); return false; }
      return true;
    });
    if (!files.length) return;
    setUploadingDocs(true);
    try {
      const collected = [...docs];
      for (const file of files) {
        try {
          const fd = new FormData();
          fd.append("file", file);
          const { data: up } = await api.post("/upload-attachment", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 60000 });
          collected.push({ file_id: up.file_id, filename: up.filename, size: up.size, content_type: up.content_type, kind: "planimetria" });
        } catch (e) { toast.error(`Caricamento fallito: ${file.name}`); }
      }
      setDocs(collected);
      if (collected.length > docs.length) toast.success("Documenti caricati.");
    } finally {
      setUploadingDocs(false);
    }
  };

  const handlePhotos = async (fileList) => {
    if (!fileList || !fileList.length || uploadingPhotos) return;
    const slots = MAX_FORM_PHOTOS - photos.length;
    if (slots <= 0) { toast.error(`Massimo ${MAX_FORM_PHOTOS} foto.`); return; }
    const files = Array.from(fileList).slice(0, slots);
    setUploadingPhotos(true);
    try {
      for (const file of files) {
        if (!file.type.startsWith("image/")) { toast.error("Solo immagini (JPG/PNG/WEBP)."); continue; }
        if (file.size > 10 * 1024 * 1024) { toast.error("Immagine troppo grande (max 10 MB)."); continue; }
        try {
          const fd = new FormData();
          fd.append("file", file);
          const { data: res } = await api.post("/upload-attachment", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 60000 });
          const preview = URL.createObjectURL(file);
          setPhotos((prev) => [...prev, { file_id: res.file_id, filename: res.filename, preview }]);
        } catch (e) { toast.error(`Caricamento fallito: ${file.name}`); }
      }
    } finally {
      setUploadingPhotos(false);
    }
  };

  const validate = () => {
    if (form.nome.trim().length < 3) return "Inserisci nome e cognome.";
    if (!form.goal) return "Seleziona il tuo vero obiettivo.";
    if (!form.rischio) return "Seleziona il tuo livello di tolleranza al rischio.";
    if (!form.intenzione) return "Indica se la valutazione è solo un'informativa o se vuoi vendere (e in quanto tempo).";
    if (!verified) return "Verificare l'indirizzo dell'immobile prima di procedere (pulsante \u201CVerifica indirizzo\u201D).";
    if (!form.tipologia) return "Seleziona la tipologia dell'immobile.";
    const mq = parseNum(form.mq);
    if (!mq || mq <= 0) return "Inserisci i metri quadri commerciali.";
    if (showFloor && !form.piano) return "Seleziona il piano dell'immobile.";
    if (showElevator && !form.ascensore) return "Indica se è presente l'ascensore.";
    if (showElevator && floorValue === "attico" && form.ascensore === "si" && !form.ascensoreInCasa) return "Indica se l'ascensore entra direttamente in casa.";
    if (showGarage && !form.garage) return "Indica se è presente garage o posto auto.";
    if (showGarage && form.garage && form.garage !== "no" && form.garageSqm.trim() && parseNum(form.garageSqm) === null) return "Mq garage non validi.";
    if (showGardenField && form.gardenSqm.trim() && parseNum(form.gardenSqm) === null) return "Superficie giardino non valida.";
    if (showTerrazzoField && form.terrazzoSqm.trim() && parseNum(form.terrazzoSqm) === null) return "Superficie terrazzo non valida.";
    if (!form.stato) return "Seleziona lo stato conservativo.";
    if (!form.esterno) return "Seleziona la condizione esterna del fabbricato.";
    if (form.usufrutto) {
      const eta = parseInt(form.usufruttoEta, 10);
      if (!eta || eta < 1 || eta > 110) return "Inserisci l'età dell'usufruttuario (1–110 anni).";
    }
    if (form.tipoValutazione === "certificata") {
      if (!form.locali.trim()) return "Premium: indica il numero di locali.";
      if (!form.bagni.trim()) return "Premium: indica il numero di bagni.";
      if (!form.riscaldamento) return "Premium: indica il tipo di riscaldamento.";
      if (!form.clima) return "Premium: indica se è presente la climatizzazione.";
      if (!form.infissi) return "Premium: indica il tipo di infissi.";
      if (!form.vista) return "Premium: indica la vista / affaccio.";
      if (!form.cantina) return "Premium: indica se sono presenti cantina o soffitta.";
      if (!form.occupazione) return "Premium: indica lo stato occupazionale.";
      if (form.annoRistr.trim() && !/^\d{4}$/.test(form.annoRistr.trim())) return "Premium: anno di ristrutturazione non valido (es. 2021).";
    }
    if (form.anno.trim() && !/^\d{3,4}$/.test(form.anno.trim())) return "Anno di costruzione non valido (es. 1985).";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) return "Inserisci un'email valida: è dove riceverai il report.";
    return null;
  };

  const premiumFields = () => (form.tipoValutazione !== "certificata" ? {} : {
    rooms: form.locali.trim() ? parseInt(form.locali, 10) || null : null,
    bathrooms: form.bagni.trim() ? parseInt(form.bagni, 10) || null : null,
    energy_class: form.ape || null,
    heating: form.riscaldamento || null,
    air_conditioning: form.clima ? form.clima === "si" : null,
    windows: form.infissi || null,
    exposure: form.espoPunti.length ? form.espoPunti.join(",") : null,
    view: form.vista || null,
    condo_fees: form.speseCond.trim() ? parseNum(form.speseCond) : null,
    cellar_attic: form.cantina ? form.cantina === "si" : null,
    occupancy: form.occupazione || null,
    last_renovation_year: /^\d{4}$/.test(form.annoRistr.trim()) ? Number(form.annoRistr.trim()) : null,
    bonus110: form.bonus110 ? form.bonus110 === "si" : null,
    garden_type: (showGardenField && form.gardenType) || null,
  });

  const buildValuationPayload = () => ({
    property_address: verified?.label || null,
    property_sqm: parseNum(form.mq),
    construction_year: /^\d{3,4}$/.test(form.anno.trim()) ? Number(form.anno.trim()) : null,
    property_category: form.tipologia || "",
    zona: form.zona || null,
    comune: verified?.city || null,
    stato_conservativo: form.stato || null,
    exterior_condition: form.esterno || null,
    usufruct: form.usufrutto || null,
    usufructuary_age: form.usufrutto && form.usufruttoEta ? parseInt(form.usufruttoEta, 10) : null,
    floor: floorValue,
    elevator: elevatorValue,
    garage: garageValue,
    garage_type: showGarage && form.garage && form.garage !== "no" ? form.garage : null,
    garage_sqm: showGarage && form.garage && form.garage !== "no" && form.garageSqm.trim() ? parseNum(form.garageSqm) : null,
    elevator_in_home: floorValue === "attico" && form.ascensore === "si" && form.ascensoreInCasa ? form.ascensoreInCasa === "si" : null,
    garden_sqm: showGardenField && form.gardenSqm.trim() ? parseNum(form.gardenSqm) : null,
    terrazzo_sqm: showTerrazzoField && form.terrazzoSqm.trim() ? parseNum(form.terrazzoSqm) : null,
    ...premiumFields(),
  });

  useEffect(() => {
    if (phase === "result" && form.tipoValutazione === "certificata" && form.email.trim()) {
      api.post("/premium-interest", { email: form.email.trim(), name: form.nome.trim() }).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase]);

  const computeValuation = async (e) => {
    e?.preventDefault();
    if (computing) return;
    const err = validate();
    if (err) { setFormError(err); return; }
    setFormError("");
    setComputing(true);
    try {
      let pa = null;
      if (photos.length) {
        try {
          const { data: res } = await api.post("/analyze-photos", {
            stato_conservativo: form.stato || null,
            photos: photos.map((p) => ({ room: "immobile", file_id: p.file_id })),
          }, { timeout: 120000 });
          pa = res;
          setPhotoAnalysis(res);
        } catch (e2) { /* non-blocking */ }
      }
      const { data: v } = await api.post("/valuation-quick", buildValuationPayload());
      if (pa && pa.consistent === false) {
        v.photo_warning = pa.warning || "Le foto non confermano lo stato conservativo dichiarato.";
        v.confidence_label = "Bassa (foto non coerenti con lo stato dichiarato)";
      }
      setValuation(v);
      if (form.tipoValutazione !== "certificata") {
        await submitLead(v);
      } else {
        setPhase("result");
        setTimeout(() => topRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 80);
      }
    } catch (err2) {
      toast.error("Calcolo non riuscito, riprova tra qualche istante.");
    } finally {
      setComputing(false);
    }
  };

  const buildLeadPayload = () => {
    const goalLabel = GOALS.find((g) => g.id === form.goal)?.short || form.goal;
    return {
      client_request_id: ensureSubmitId(),
      goal: form.goal,
      goal_label: goalLabel,
      risk_tolerance: form.rischio || null,
      sale_intent: form.intenzione || null,
      exterior_condition: form.esterno || null,
      usufruct: form.usufrutto || null,
      usufructuary_age: form.usufrutto && form.usufruttoEta ? parseInt(form.usufruttoEta, 10) : null,
      owner_name: form.nome.trim(),
      property_address: verified?.label || null,
      property_type: form.tipologia || null,
      property_sqm: parseNum(form.mq),
      construction_year: /^\d{3,4}$/.test(form.anno.trim()) ? Number(form.anno.trim()) : null,
      zona: form.zona || null,
      comune: verified?.city || null,
      address_verified: !!verified,
      floor: floorValue,
      elevator: elevatorValue,
      garage: garageValue,
      ...premiumFields(),
      energy_class: form.ape || null,
      exposure: form.espoPunti.length ? form.espoPunti.join(",") : null,
      bonus110: form.bonus110 ? form.bonus110 === "si" : null,
      garden_type: (showGardenField && form.gardenType) || null,
      garage_type: showGarage && form.garage && form.garage !== "no" ? form.garage : null,
      garage_sqm: showGarage && form.garage && form.garage !== "no" && form.garageSqm.trim() ? parseNum(form.garageSqm) : null,
      elevator_in_home: floorValue === "attico" && form.ascensore === "si" && form.ascensoreInCasa ? form.ascensoreInCasa === "si" : null,
      geo_from_photo: false,
      gps_match: null,
      photo_gps_label: null,
      valuation_mode: form.tipoValutazione,
      photo_tier: form.tipoValutazione === "certificata" ? "premium" : "base",
      stato_conservativo: form.stato || null,
      garden_sqm: showGardenField && form.gardenSqm.trim() ? parseNum(form.gardenSqm) : null,
      terrazzo_sqm: showTerrazzoField && form.terrazzoSqm.trim() ? parseNum(form.terrazzoSqm) : null,
      notes: form.note.trim() || null,
      full_name: form.nome.trim(),
      email: form.email.trim(),
      phone: form.telefono.trim() || null,
      attached_files: docs,
      photos: photos.map((p) => ({ room: "immobile", file_id: p.file_id, filename: p.filename })),
      photo_analysis: photoAnalysis || null,
      beta: !!beta,
      valuation: valuation || null,
      conversation_log: [],
    };
  };

  const submitLead = async (vOverride = null) => {
    if (submitting) return;
    setSubmitting(true);
    try {
      const payload = buildLeadPayload();
      if (vOverride) payload.valuation = vOverride;
      const { data: created } = await api.post("/chatbot-lead", payload);
      setLeadId(created?.id || null);
      submitIdRef.current = null;
      setPhase("done");
      try { localStorage.removeItem(DRAFT_KEY); } catch (e) { /* noop */ }
      toast.success("Dossier trasmesso.");
      setTimeout(() => topRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 80);
    } catch (err) {
      console.error("submitLead failed:", err);
      toast.error("Trasmissione fallita. Riprova o contattaci a velecinquesrl@gmail.com.");
    } finally {
      setSubmitting(false);
    }
  };

  const payPremium = async () => {
    if (paying) return;
    const errs = {};
    if (fiscal.name.trim().length < 2) errs.name = "Inserisci il Nome o la Ragione Sociale";
    if (fiscal.address.trim().length < 5) errs.address = "Inserisci l'indirizzo completo (via, civico, CAP, città)";
    const tc = fiscal.taxCode.trim().toUpperCase().replace(/\s/g, "");
    const isPiva = /^[0-9]{11}$/.test(tc);
    const isCf = /^[A-Z]{6}[0-9LMNPQRSTUV]{2}[ABCDEHLMPRST][0-9LMNPQRSTUV]{2}[A-Z][0-9LMNPQRSTUV]{3}[A-Z]$/.test(tc);
    if (!isPiva && !isCf) errs.taxCode = "Codice Fiscale (16 caratteri) o P.IVA (11 cifre) non valido";
    setFiscalErr(errs);
    if (Object.keys(errs).length) {
      toast.error("Controlla i dati di fatturazione evidenziati in rosso.");
      return;
    }
    setPaying(true);
    try {
      let id = leadId;
      if (!id) {
        const { data: created } = await api.post("/chatbot-lead", { ...buildLeadPayload(), payment_pending: true });
        id = created?.id || null;
        setLeadId(id);
        submitIdRef.current = null;
      }
      if (!id) throw new Error("lead");
      const { data: co } = await api.post("/paypal/create-order", {
        lead_id: id,
        billing_name: fiscal.name.trim(),
        billing_address: fiscal.address.trim(),
        tax_code: tc,
        origin_url: window.location.origin,
        visura_requested: visuraReq,
      });
      window.location.href = co.approval_url;
    } catch (e) {
      console.error("payPremium failed:", e);
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Impossibile avviare il pagamento in questo momento, riprova tra qualche istante.");
      setPaying(false);
    }
  };

  const downloadPdf = async () => {
    try {
      const payload = {
        full_name: form.nome.trim() || "Cliente",
        property_address: verified?.label || null,
        property_type: form.tipologia || null,
        property_sqm: parseNum(form.mq),
        construction_year: /^\d{3,4}$/.test(form.anno.trim()) ? Number(form.anno.trim()) : null,
        stato_conservativo: form.stato || null,
        floor: floorValue,
        elevator: elevatorValue,
        garage: garageValue,
        garden_sqm: showGardenField && form.gardenSqm.trim() ? parseNum(form.gardenSqm) : null,
        terrazzo_sqm: showTerrazzoField && form.terrazzoSqm.trim() ? parseNum(form.terrazzoSqm) : null,
        valuation,
        photo_analysis: photoAnalysis,
        lead_id: leadId,
      };
      const res = await api.post("/valuation-pdf", payload, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = "Valutazione-MCM-HomeExchange.pdf";
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Download PDF non riuscito.");
    }
  };

  const protocolCode = leadId ? ("HEC-" + leadId.replace(/[^a-zA-Z0-9]/g, "").slice(-6).toUpperCase()) : "HEC-•••";
  const firstName = form.nome.trim().split(" ")[0] || "";

  const shareWhatsApp = () => {
    api.post("/share-event", { lead_id: leadId, protocol_code: protocolCode, channel: "whatsapp", beta: !!beta }).catch(() => {});
    const v = valuation || {};
    const txt = v.estimated_value_median
      ? `🏠 Ho appena scoperto quanto vale casa mia con il Copilot AI di Gruppo Vela: € ${new Intl.NumberFormat("it-IT").format(v.estimated_value_low)} – € ${new Intl.NumberFormat("it-IT").format(v.estimated_value_high)}. Valutazione gratuita in 2 minuti, provala anche tu 👉 https://eneaspecialist.it/valutazione`
      : `🏠 Ho provato il Copilot AI di Gruppo Vela: valutazione immobiliare gratuita in 2 minuti, basata sulle quotazioni OMI ufficiali. Provala anche tu 👉 https://eneaspecialist.it/valutazione`;
    window.open(`https://wa.me/?text=${encodeURIComponent(txt)}`, "_blank", "noopener");
  };

  const submitCallRequest = async () => {
    if (callSubmitting) return;
    if (!callDate || !callSlot) { toast.error("Seleziona giorno e fascia oraria."); return; }
    setCallSubmitting(true);
    try {
      await api.post("/call-request", {
        lead_id: leadId, protocol_code: protocolCode,
        full_name: form.nome.trim() || "Cliente", email: form.email.trim(), phone: form.telefono.trim() || null,
        preferred_date: callDate, preferred_slot: callSlot,
      });
      setCallDone(true);
      toast.success("Richiesta di call inviata.");
    } catch (e) {
      toast.error("Invio richiesta non riuscito. Riprova.");
    } finally {
      setCallSubmitting(false);
    }
  };

  const requestUpgrade = async (tier) => {
    if (upgradeSubmitting) return;
    setUpgradeSubmitting(true);
    try {
      await api.post("/upgrade-request", {
        lead_id: leadId, protocol_code: protocolCode,
        full_name: form.nome.trim() || "Cliente", email: form.email.trim(), phone: form.telefono.trim() || null,
        requested_tier: tier, current_tier: form.tipoValutazione,
      });
      setUpgradeDone(tier);
      toast.success("Richiesta inviata! Ti ricontattiamo a breve.");
    } catch (e) {
      toast.error("Invio non riuscito, riprova.");
    } finally {
      setUpgradeSubmitting(false);
    }
  };

  const submitBetaFeedback = async () => {
    if (feedbackSubmitting) return;
    if (!easeRating || !trustRating) { toast.error("Seleziona le stelle per entrambe le domande."); return; }
    setFeedbackSubmitting(true);
    try {
      await api.post("/beta-feedback", {
        lead_id: leadId, protocol_code: protocolCode,
        ease_rating: easeRating, trust_rating: trustRating, comments: feedbackText.trim() || null,
      });
      setFeedbackDone(true);
      toast.success("Grazie per il tuo feedback! 🙏");
    } catch (e) {
      toast.error("Invio feedback non riuscito, riprova.");
    } finally {
      setFeedbackSubmitting(false);
    }
  };

  useEffect(() => {
    if (phase === "result" && form.nome.trim().length >= 3) {
      setFiscal((f) => (f.name ? f : { ...f, name: form.nome.trim() }));
    }
  }, [phase]); // eslint-disable-line react-hooks/exhaustive-deps

  const formProgress = (() => {
    const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim());
    const base = [form.nome.trim().length >= 3, !!form.goal, !!form.rischio, !!form.intenzione, !!verified, !!form.tipologia,
      !!parseNum(form.mq), !showFloor || !!form.piano, !showElevator || !!form.ascensore,
      !showGarage || !!form.garage, !!form.stato, !!form.esterno, emailOk];
    const prem = form.tipoValutazione === "certificata"
      ? [!!form.locali.trim(), !!form.bagni.trim(), !!form.ape, !!form.riscaldamento, !!form.clima,
         !!form.infissi, !!form.esposizione, !!form.vista, !!form.cantina, !!form.occupazione]
      : [];
    const all = [...base, ...prem];
    return Math.round((all.filter(Boolean).length / all.length) * 100);
  })();

  const restart = () => {
    try { localStorage.removeItem(DRAFT_KEY); } catch (e) { /* noop */ }
    window.location.reload();
  };

  return (
    <div ref={topRef} className="relative bg-white rounded-2xl border border-slate-200 shadow-2xl shadow-slate-200/60 overflow-hidden" data-testid="valuation-flow">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 bg-[#0b2545] text-white">
        <div className="flex items-center gap-3">
          <span className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#A9D6E5] to-[#14B8A6] flex items-center justify-center flex-shrink-0">
            <ClipboardList className="w-5 h-5 text-[#0b2545]" />
          </span>
          <div>
            <div className="text-sm font-semibold tracking-tight">Scheda di valutazione immobiliare</div>
            <div className="text-[11px] text-[#A9D6E5]">
              {phase === "form" && "Compila il modulo · ~2 minuti"}
              {phase === "result" && "Diagnostica Enea completata"}
              {phase === "done" && "Dossier protocollato ✓"}
            </div>
          </div>
        </div>
        {draftRestored && phase === "form" && (
          <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-amber-200 bg-amber-500/20 rounded-full px-2 py-0.5" data-testid="val-draft-restored">
            💾 Bozza recuperata
          </span>
        )}
      </div>
      {phase === "form" && (
        <div className="px-6 py-2 bg-[#0b2545]/95 border-t border-white/10" data-testid="val-progress-bar">
          <div className="flex items-center justify-between mb-1">
            <span className="text-[10px] text-[#A9D6E5]">Completamento modulo</span>
            <span className="text-[10px] font-bold text-[#14B8A6]" data-testid="val-progress-pct">{formProgress}%</span>
          </div>
          <div className="h-1.5 rounded-full bg-white/10 overflow-hidden">
            <div className="h-full rounded-full bg-gradient-to-r from-[#14B8A6] to-[#2dd4bf] transition-all duration-500" style={{ width: `${formProgress}%` }} />
          </div>
        </div>
      )}

      {/* ===== FORM ===== */}
      {phase === "form" && (
        <form onSubmit={computeValuation} noValidate className="p-4 sm:p-6 space-y-5 bg-slate-50/60" data-testid="valuation-form">
          {/* Chi sei */}
          <FormSection n="01" title="Chi sei">
            <div>
              <label className={labelCls}>Nome e cognome del proprietario *</label>
              <div className="relative">
                <input value={form.nome} onChange={(e) => setF({ nome: e.target.value })}
                  placeholder="Es. Mario Rossi (se più proprietari, scrivili tutti)" className={inputCls + " pr-9"} data-testid="val-nome" />
                <MicButton onText={(t) => setF({ nome: (form.nome.trim() ? form.nome.trim() + " " : "") + t })}
                  testId="val-mic-nome" className="absolute right-1.5 top-1/2 -translate-y-1/2" />
              </div>
            </div>
          </FormSection>

          {/* Indirizzo */}
          <FormSection n="02" title="Dove si trova l'immobile">
            <div className="relative">
              <label className={labelCls}>Cerca l&apos;indirizzo — i campi si compilano da soli ✨</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                <input value={searchQ} onChange={(e) => setSearchQ(e.target.value)}
                  onFocus={() => searchResults.length && setSearchOpen(true)}
                  placeholder="Es. Via Puccini, Padova"
                  className={inputCls + " pl-9 pr-14"} autoComplete="off" data-testid="val-addr-search" />
                <MicButton onText={(t) => setSearchQ(t)} testId="val-mic-indirizzo"
                  className="absolute right-8 top-1/2 -translate-y-1/2" />
                {searchLoading && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-[#14B8A6]" />}
              </div>
              {searchOpen && searchResults.length > 0 && (
                <div className="absolute z-20 mt-1 w-full bg-white border border-slate-200 rounded-lg shadow-xl overflow-hidden" data-testid="val-addr-suggestions">
                  {searchResults.map((s, i) => (
                    <button key={i} type="button" onClick={() => pickSuggestion(s)}
                      className="w-full text-left px-3 py-2.5 text-sm text-[#0b2545] hover:bg-[#14B8A6]/10 flex items-center gap-2 border-b border-slate-100 last:border-0"
                      data-testid={`val-addr-suggestion-${i}`}>
                      <MapPin className="w-3.5 h-3.5 text-[#14B8A6] flex-shrink-0" />
                      <span className="truncate">{s.label}</span>
                    </button>
                  ))}
                </div>
              )}
              <p className="mt-1 text-[10px] text-slate-400">Scrivi via e città (senza numero civico), poi scegli dalla lista: provincia, comune e via si compilano da soli. Il numero civico lo aggiungi subito dopo. Oppure compila i campi qui sotto a mano.</p>
            </div>
            <div className="grid grid-cols-2 gap-2.5">
              <div>
                <label className={labelCls}>Provincia *</label>
                <input value={addr.provincia} onChange={(e) => setA({ provincia: e.target.value })}
                  placeholder="Es. PD o Padova" className={addrInputCls("provincia")} data-testid="val-addr-provincia" />
              </div>
              <div>
                <label className={labelCls}>Comune *</label>
                <input value={addr.comune} onChange={(e) => setA({ comune: e.target.value })}
                  placeholder="Es. Padova" className={addrInputCls("comune")} data-testid="val-addr-comune" />
              </div>
              <div>
                <label className={labelCls}>Via / Piazza *</label>
                <input value={addr.via} onChange={(e) => setA({ via: e.target.value })}
                  placeholder="Es. Via Roma" className={addrInputCls("via")} data-testid="val-addr-via" />
              </div>
              <div>
                <label className={labelCls}>N. civico *</label>
                <input value={addr.civico} onChange={(e) => setA({ civico: e.target.value })}
                  placeholder="Es. 12" inputMode="numeric" className={addrInputCls("civico")} data-testid="val-addr-civico" />
              </div>
            </div>
            {addrError && (
              <div className="space-y-1.5" data-testid="val-addr-error-box">
                <p className="flex items-start gap-1.5 text-xs text-red-600" data-testid="val-addr-error">
                  <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" /> {addrError}
                </p>
                {addrSuggestions.length > 0 && (
                  <div className="flex flex-wrap items-center gap-1.5" data-testid="val-addr-fix-suggestions">
                    <span className="text-[10px] text-slate-500 font-semibold">Clicca per correggere:</span>
                    {addrSuggestions.map((s) => (
                      <button key={s} type="button" onClick={() => applySuggestion(s)}
                        className="px-2.5 py-1 rounded-full bg-[#14B8A6]/10 border border-[#14B8A6]/40 text-xs font-medium text-[#0d9488] hover:bg-[#14B8A6]/20 transition"
                        data-testid="val-addr-suggestion-chip">
                        {s}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
            {verified ? (
              <div className="flex items-start gap-2 p-2.5 rounded-lg bg-[#14B8A6]/10 border border-[#14B8A6]/30 text-xs text-[#0b2545]" data-testid="val-addr-verified">
                <CheckCircle2 className="w-4 h-4 text-[#14B8A6] flex-shrink-0 mt-0.5" />
                <span>Indirizzo verificato sulle mappe: <b>{verified.label}</b></span>
              </div>
            ) : (
              <button type="button" onClick={verifyAddress} disabled={addrVerifying}
                className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-[#0b2545] text-white text-sm font-semibold hover:bg-[#13315c] disabled:opacity-60"
                data-testid="val-addr-verify">
                {addrVerifying ? <Loader2 className="w-4 h-4 animate-spin" /> : <MapPin className="w-4 h-4" />}
                Verifica indirizzo
              </button>
            )}
            <div>
              <label className={labelCls}>Zona (se la conosci)</label>
              <select value={form.zona} onChange={(e) => setF({ zona: e.target.value })} className={inputCls} data-testid="val-zona">
                <option value="">Non sono sicuro — determinatela voi</option>
                {ZONE_TIERS.map((z) => <option key={z.id} value={z.id}>{z.label}</option>)}
              </select>
            </div>
          </FormSection>

          {/* Immobile */}
          <FormSection n="03" title="L'immobile">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className={labelCls}>Tipologia *</label>
                <select value={form.tipologia}
                  onChange={(e) => setF({ tipologia: e.target.value, piano: "", ascensore: "", ascensoreInCasa: "", garage: "", garageSqm: "", hasGarden: false, gardenSqm: "", hasTerrazzo: false, terrazzoSqm: "" })}
                  className={inputCls} data-testid="val-tipologia">
                  <option value="">Seleziona…</option>
                  {PROPERTY_TYPES.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
                </select>
              </div>
              <div>
                <label className={labelCls}>Superficie commerciale (mq) *</label>
                <input value={form.mq} onChange={(e) => setF({ mq: e.target.value })}
                  placeholder="Es. 120" inputMode="numeric" className={inputCls} data-testid="val-mq" />
                <p className="mt-1 flex items-start gap-1 text-[10px] text-slate-500" data-testid="val-mq-help">
                  <Info className="w-3 h-3 flex-shrink-0 mt-px text-[#14B8A6]" />
                  La superficie commerciale = calpestabile + muri interni e perimetrali (di norma +10–15%). È quella su atto o visura; se non la conosci, aggiungi ~10% ai mq calpestabili.
                </p>
              </div>
            </div>

            {(showFloor || showElevator || showGarage) && (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {showFloor && (
                  <div>
                    <label className={labelCls}>Piano *</label>
                    <select value={form.piano} onChange={(e) => setF({ piano: e.target.value, ascensoreInCasa: "" })} className={inputCls} data-testid="val-piano">
                      <option value="">Seleziona…</option>
                      {FLOORS.map((f) => <option key={f.id} value={f.id}>{f.label}</option>)}
                    </select>
                  </div>
                )}
                {showElevator && (
                  <div>
                    <label className={labelCls}>Ascensore *</label>
                    <select value={form.ascensore} onChange={(e) => setF({ ascensore: e.target.value, ascensoreInCasa: "" })} className={inputCls} data-testid="val-ascensore">
                      <option value="">Seleziona…</option>
                      <option value="si">Sì</option>
                      <option value="no">No</option>
                    </select>
                  </div>
                )}
                {showGarage && (
                  <div>
                    <label className={labelCls}>Garage / posto auto *</label>
                    <select value={form.garage} onChange={(e) => setF({ garage: e.target.value, garageSqm: e.target.value !== "no" ? form.garageSqm : "" })} className={inputCls} data-testid="val-garage">
                      <option value="">Seleziona…</option>
                      <option value="garage">Sì — garage</option>
                      <option value="posto_auto">Sì — posto auto</option>
                      <option value="no">No</option>
                    </select>
                  </div>
                )}
              </div>
            )}

            {((showElevator && floorValue === "attico" && form.ascensore === "si") || (showGarage && form.garage && form.garage !== "no")) && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {showElevator && floorValue === "attico" && form.ascensore === "si" && (
                  <div>
                    <label className={labelCls}>L'ascensore entra direttamente in casa? *</label>
                    <select value={form.ascensoreInCasa} onChange={(e) => setF({ ascensoreInCasa: e.target.value })} className={inputCls} data-testid="val-ascensore-in-casa">
                      <option value="">Seleziona…</option>
                      <option value="si">Sì — sbarca dentro l'appartamento</option>
                      <option value="no">No — sbarca sul pianerottolo</option>
                    </select>
                  </div>
                )}
                {showGarage && form.garage && form.garage !== "no" && (
                  <div>
                    <label className={labelCls}>Mq garage/posto auto (facoltativo)</label>
                    <input value={form.garageSqm} onChange={(e) => setF({ garageSqm: e.target.value })}
                      placeholder={form.garage === "posto_auto" ? "Es. 12" : "Es. 15"} inputMode="numeric" className={inputCls} data-testid="val-garage-sqm" />
                    <p className="mt-1 text-[10px] text-slate-500">Serve per valutare il garage a corpo con le quotazioni OMI Box della zona. Se vuoto usiamo {form.garage === "posto_auto" ? "12" : "15"} mq standard.</p>
                  </div>
                )}
              </div>
            )}

            {(showGardenCheckbox || showTerrazzoCheckbox) && (
              <div className="flex flex-wrap gap-x-6 gap-y-2">
                {showGardenCheckbox && (
                  <label className="inline-flex items-center gap-2 text-sm text-[#0b2545] cursor-pointer select-none">
                    <input type="checkbox" checked={form.hasGarden}
                      onChange={(e) => setF({ hasGarden: e.target.checked, gardenSqm: e.target.checked ? form.gardenSqm : "" })}
                      className="w-4 h-4 rounded border-slate-300 accent-[#14B8A6]" data-testid="val-garden-checkbox" />
                    Con giardino
                  </label>
                )}
                {showTerrazzoCheckbox && (
                  <label className="inline-flex items-center gap-2 text-sm text-[#0b2545] cursor-pointer select-none">
                    <input type="checkbox" checked={form.hasTerrazzo}
                      onChange={(e) => setF({ hasTerrazzo: e.target.checked, terrazzoSqm: e.target.checked ? form.terrazzoSqm : "" })}
                      className="w-4 h-4 rounded border-slate-300 accent-[#14B8A6]" data-testid="val-terrazzo-checkbox" />
                    Con terrazzo
                  </label>
                )}
              </div>
            )}

            {(showGardenField || showTerrazzoField) && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {showGardenField && (
                  <div>
                    <label className={labelCls}>Superficie giardino (mq)</label>
                    <input value={form.gardenSqm} onChange={(e) => setF({ gardenSqm: e.target.value })}
                      placeholder="Es. 300" inputMode="numeric" className={inputCls} data-testid="val-garden-sqm" />
                    <select value={form.gardenType} onChange={(e) => setF({ gardenType: e.target.value })}
                      className={inputCls + " mt-2"} data-testid="val-garden-type">
                      <option value="">Giardino privato o condominiale?</option>
                      <option value="privato">Privato (uso esclusivo)</option>
                      <option value="condominiale">Condominiale</option>
                    </select>
                  </div>
                )}
                {showTerrazzoField && (
                  <div>
                    <label className={labelCls}>Superficie terrazzo (mq)</label>
                    <input value={form.terrazzoSqm} onChange={(e) => setF({ terrazzoSqm: e.target.value })}
                      placeholder="Es. 15" inputMode="numeric" className={inputCls} data-testid="val-terrazzo-sqm" title={TERRAZZO_TOOLTIP} />
                    <p className="mt-1 flex items-start gap-1 text-[10px] text-slate-500" data-testid="val-terrazzo-tooltip">
                      <Info className="w-3 h-3 flex-shrink-0 mt-px text-[#14B8A6]" /> {TERRAZZO_TOOLTIP}
                    </p>
                  </div>
                )}
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className={labelCls}>Stato conservativo *</label>
                <select value={form.stato} onChange={(e) => setF({ stato: e.target.value })} className={inputCls} data-testid="val-stato">
                  <option value="">Seleziona…</option>
                  {STATO_CONSERVATIVO.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
                </select>
              </div>
              <div>
                <label className={labelCls}>Anno di costruzione</label>
                <input value={form.anno} onChange={(e) => setF({ anno: e.target.value })}
                  placeholder="Es. 1985 (opzionale)" inputMode="numeric" className={inputCls} data-testid="val-anno" />
              </div>
            </div>
            <div>
              <label className={labelCls}>Condizione esterna del fabbricato *</label>
              <select value={form.esterno} onChange={(e) => setF({ esterno: e.target.value })} className={inputCls} data-testid="val-esterno">
                <option value="">Seleziona…</option>
                {EXTERIOR_OPTIONS.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
              </select>
              <p className="mt-1 flex items-start gap-1 text-[10px] text-slate-500" data-testid="val-esterno-hint">
                <Info className="w-3 h-3 flex-shrink-0 mt-px text-[#14B8A6]" /> Facciate, tinteggiatura e cappotto incidono sul valore: la condizione esterna entra nel calcolo.
              </p>
            </div>
            <div className="rounded-xl border border-slate-200 bg-white p-3.5">
              <label className="flex items-center gap-3 cursor-pointer" data-testid="val-usufrutto-check">
                <input type="checkbox" checked={form.usufrutto}
                  onChange={(e) => setF({ usufrutto: e.target.checked, usufruttoEta: e.target.checked ? form.usufruttoEta : "" })}
                  className="accent-[#14B8A6] w-4 h-4 flex-shrink-0" />
                <span className="text-sm font-medium text-[#0b2545]">C&apos;è un usufruttuario sull&apos;immobile? <span className="text-slate-500 font-normal">(usufrutto / nuda proprietà)</span></span>
              </label>
              {form.usufrutto && (
                <div className="mt-3">
                  <label className={labelCls}>Età dell&apos;usufruttuario *</label>
                  <input value={form.usufruttoEta} onChange={(e) => setF({ usufruttoEta: e.target.value.replace(/\D/g, "").slice(0, 3) })}
                    placeholder="Es. 74" inputMode="numeric" className={inputCls} data-testid="val-usufrutto-eta" />
                  <p className="mt-1 text-[10px] text-slate-500" data-testid="val-usufrutto-hint">
                    Riceverai anche il valore della <b>nuda proprietà</b>, calcolato con i coefficienti ufficiali 2026 (D.Lgs. 139/2024).
                  </p>
                </div>
              )}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className={labelCls}>Classe energetica APE (opzionale)</label>
                <select value={form.ape} onChange={(e) => setF({ ape: e.target.value })} className={inputCls} data-testid="val-ape">
                  <option value="">Seleziona…</option>
                  {APE_OPTIONS.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
                </select>
              </div>
              <div>
                <label className={labelCls}>È stato fatto il Bonus 110? (opzionale)</label>
                <select value={form.bonus110} onChange={(e) => setF({ bonus110: e.target.value })} className={inputCls} data-testid="val-bonus110">
                  <option value="">Seleziona…</option>
                  <option value="si">Sì</option>
                  <option value="no">No</option>
                </select>
              </div>
            </div>
            <div>
              <label className={labelCls}>Esposizione (opzionale — seleziona uno o più lati)</label>
              <div className="flex flex-wrap gap-x-5 gap-y-2" data-testid="val-esposizione-punti">
                {["nord", "sud", "est", "ovest"].map((p) => (
                  <label key={p} className="inline-flex items-center gap-2 text-sm text-[#0b2545] cursor-pointer select-none">
                    <input type="checkbox" checked={form.espoPunti.includes(p)}
                      onChange={(e) => setF({ espoPunti: e.target.checked ? [...form.espoPunti, p] : form.espoPunti.filter((x) => x !== p) })}
                      className="w-4 h-4 rounded border-slate-300 accent-[#14B8A6]" data-testid={`val-espo-${p}`} />
                    {p.charAt(0).toUpperCase() + p.slice(1)}
                  </label>
                ))}
              </div>
            </div>
            <div>
              <label className={labelCls}>Note particolari</label>
              <div className="relative">
                <input value={form.note} onChange={(e) => setF({ note: e.target.value })}
                  placeholder="Ristrutturazioni, vincoli… (opzionale)" className={inputCls + " pr-9"} data-testid="val-note" />
                <MicButton onText={(t) => setF({ note: (form.note.trim() ? form.note.trim() + " " : "") + t })}
                  testId="val-mic-note" className="absolute right-1.5 top-1/2 -translate-y-1/2" />
              </div>
            </div>
          </FormSection>

          {/* Tipo valutazione */}
          {/* Domande qualificanti */}
          <FormSection n="04" title="Le domande che contano">
            <div>
              <label className={labelCls}>Qual è il tuo vero obiettivo? *</label>
              <div className="space-y-2">
                {GOALS.map((g) => (
                  <label key={g.id}
                    className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition ${form.goal === g.id ? "border-[#14B8A6] bg-[#14B8A6]/[0.06]" : "border-slate-200 bg-white hover:border-[#A9D6E5]"}`}
                    data-testid={`val-goal-${g.id}`}>
                    <input type="radio" name="goal" value={g.id} checked={form.goal === g.id}
                      onChange={() => setF({ goal: g.id })} className="accent-[#14B8A6]" />
                    <span className="text-sm font-medium text-[#0b2545]">{g.label}</span>
                  </label>
                ))}
              </div>
            </div>
            <div>
              <label className={labelCls}>Qual è il tuo livello di tolleranza al rischio? *</label>
              <div className="space-y-2">
                {RISK_OPTIONS.map((r) => (
                  <label key={r.id}
                    className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition ${form.rischio === r.id ? "border-[#14B8A6] bg-[#14B8A6]/[0.06]" : "border-slate-200 bg-white hover:border-[#A9D6E5]"}`}
                    data-testid={`val-rischio-${r.id}`}>
                    <input type="radio" name="rischio" value={r.id} checked={form.rischio === r.id}
                      onChange={() => setF({ rischio: r.id })} className="accent-[#14B8A6] flex-shrink-0" />
                    <span className="text-sm font-medium text-[#0b2545]">{r.label}</span>
                  </label>
                ))}
              </div>
            </div>
            <div>
              <label className={labelCls}>Questa valutazione è solo un&apos;informativa o vuoi davvero vendere? *</label>
              <div className="space-y-2">
                {SALE_INTENTS.map((s) => (
                  <label key={s.id}
                    className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition ${form.intenzione === s.id ? "border-[#14B8A6] bg-[#14B8A6]/[0.06]" : "border-slate-200 bg-white hover:border-[#A9D6E5]"}`}
                    data-testid={`val-intenzione-${s.id}`}>
                    <input type="radio" name="intenzione" value={s.id} checked={form.intenzione === s.id}
                      onChange={() => setF({ intenzione: s.id })} className="accent-[#14B8A6] flex-shrink-0" />
                    <span className="text-sm font-medium text-[#0b2545]">
                      {s.label}
                      {s.hot && <span className="ml-1.5 text-[10px] font-bold text-amber-600 whitespace-nowrap">🔥 corsia prioritaria</span>}
                    </span>
                  </label>
                ))}
              </div>
              <p className="mt-1 text-[10px] text-slate-500" data-testid="val-intenzione-hint">
                Chi vende subito o entro 3 mesi riceve la bozza di valutazione accurata e la presa in carico prioritaria del team.
              </p>
            </div>
          </FormSection>

          <FormSection n="05" title="Tipo di valutazione">
            <div className="space-y-2">
              {VALUATION_TYPES.map((v) => (
                <label key={v.id}
                  className={`flex items-start gap-3 p-3 rounded-xl border cursor-pointer transition ${form.tipoValutazione === v.id ? "border-[#14B8A6] bg-[#14B8A6]/[0.06]" : "border-slate-200 bg-white hover:border-[#A9D6E5]"}`}
                  data-testid={`val-tipo-valutazione-${v.id}`}>
                  <input type="radio" name="tipoValutazione" value={v.id} checked={form.tipoValutazione === v.id}
                    onChange={() => setF({ tipoValutazione: v.id })} className="mt-1 accent-[#14B8A6]" />
                  <span className="flex-1">
                    <span className="block text-sm font-semibold text-[#0b2545]">
                      {v.label} <span className={`ml-1.5 text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-full font-bold ${v.id === "classica" ? "bg-[#14B8A6]/15 text-[#0d9488]" : "bg-[#0b2545] text-[#A9D6E5]"}`}>{v.id === "certificata" && launchActive ? `€${premiumPrice}` : v.price}</span>
                      {v.id === "certificata" && launchActive && (
                        <span className="ml-1.5 align-middle whitespace-nowrap">
                          <span className="text-[10px] text-slate-400 line-through">€279</span>
                          <span className="ml-1 text-[10px] font-bold text-amber-600" data-testid="val-launch-remaining">🔥 Offerta lancio: rimasti {offer.remaining} posti su {offer.quota} a €{offer.price_eur}</span>
                        </span>
                      )}
                      {v.id === "certificata" && offer && !launchActive && (
                        <span className="ml-1.5 text-[10px] font-semibold text-slate-500" data-testid="val-standard-price">Prezzo standard: €279</span>
                      )}
                    </span>
                    <span className="block text-[11px] text-slate-500 mt-0.5 leading-snug">{v.desc}</span>
                  </span>
                </label>
              ))}
            </div>
            {form.tipoValutazione === "certificata" && (
              <div className="rounded-lg border border-dashed border-[#14B8A6]/50 bg-[#14B8A6]/[0.04] p-3" data-testid="val-docs-section">
                <div className="text-xs font-semibold text-[#0b2545] mb-1.5">Documenti catastali (visura · planimetrie · carta d&apos;identità)</div>
                <p className="text-[11px] text-slate-500 mb-2">Se non hai la visura, allega la carta d&apos;identità: la richiediamo noi per te a €5.</p>
                <div className="flex flex-wrap items-center gap-2">
                  <button type="button" onClick={() => docsInputRef.current?.click()} disabled={uploadingDocs}
                    className="inline-flex items-center gap-2 px-3.5 py-2 rounded-lg bg-[#14B8A6] text-white text-xs font-semibold hover:bg-[#0d9488] disabled:opacity-60"
                    data-testid="val-docs-upload">
                    {uploadingDocs ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Paperclip className="w-3.5 h-3.5" />}
                    Carica documenti
                  </button>
                  {docs.length > 0 && (
                    <span className="inline-flex items-center gap-1.5 text-xs text-[#0b2545]" data-testid="val-docs-count">
                      <FileText className="w-3.5 h-3.5 text-[#14B8A6]" /> {docs.length} document{docs.length === 1 ? "o" : "i"}
                      <button type="button" onClick={() => setDocs([])} className="text-slate-400 hover:text-slate-700" data-testid="val-docs-clear">
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </span>
                  )}
                </div>
                <input ref={docsInputRef} type="file" multiple accept="application/pdf,image/jpeg,image/jpg,image/png,image/webp"
                  className="hidden" onChange={(e) => { handleDocs(e.target.files); e.target.value = ""; }} data-testid="val-docs-input" />
              </div>
            )}
          </FormSection>

          {/* Fattori Premium */}
          {form.tipoValutazione === "certificata" && (
            <FormSection n="06" title="Fattori Premium — i 22 dati che incidono fino al 40%">
              <div className="rounded-lg bg-[#0b2545] text-white px-4 py-3 text-xs leading-relaxed" data-testid="val-premium-intro">
                💎 <b>Stai investendo €{premiumPrice}: diamo valore al tuo denaro.</b> Ogni campo che completi affina il valore reale
                del tuo immobile — completi, i 22 fattori incidono <b>fino al 40%</b> del risultato finale, e ognuno viene
                spiegato nella tua brochure.
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className={labelCls}>Numero locali *</label>
                  <input value={form.locali} onChange={(e) => setF({ locali: e.target.value })}
                    placeholder="Es. 4" inputMode="numeric" className={inputCls} data-testid="val-locali" />
                </div>
                <div>
                  <label className={labelCls}>Numero bagni * · il 2° vale +2%</label>
                  <input value={form.bagni} onChange={(e) => setF({ bagni: e.target.value })}
                    placeholder="Es. 2" inputMode="numeric" className={inputCls} data-testid="val-bagni" />
                </div>
                <div>
                  <label className={labelCls}>Riscaldamento * · fino a ±3%</label>
                  <select value={form.riscaldamento} onChange={(e) => setF({ riscaldamento: e.target.value })} className={inputCls} data-testid="val-riscaldamento">
                    <option value="">Seleziona…</option>
                    {HEATING_OPTIONS.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className={labelCls}>Climatizzazione * · +1%</label>
                  <select value={form.clima} onChange={(e) => setF({ clima: e.target.value })} className={inputCls} data-testid="val-clima">
                    <option value="">Seleziona…</option>
                    <option value="si">Sì</option>
                    <option value="no">No</option>
                  </select>
                </div>
                <div>
                  <label className={labelCls}>Infissi * · fino a ±2%</label>
                  <select value={form.infissi} onChange={(e) => setF({ infissi: e.target.value })} className={inputCls} data-testid="val-infissi">
                    <option value="">Seleziona…</option>
                    {WINDOWS_OPTIONS.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className={labelCls}>Vista / affaccio * · fino a ±3%</label>
                  <select value={form.vista} onChange={(e) => setF({ vista: e.target.value })} className={inputCls} data-testid="val-vista">
                    <option value="">Seleziona…</option>
                    {VIEW_OPTIONS.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className={labelCls}>Spese condominiali €/mese</label>
                  <input value={form.speseCond} onChange={(e) => setF({ speseCond: e.target.value })}
                    placeholder="Es. 120 (opzionale)" inputMode="numeric" className={inputCls} data-testid="val-spese-cond" />
                </div>
                <div>
                  <label className={labelCls}>Cantina / soffitta * · +1%</label>
                  <select value={form.cantina} onChange={(e) => setF({ cantina: e.target.value })} className={inputCls} data-testid="val-cantina">
                    <option value="">Seleziona…</option>
                    <option value="si">Sì</option>
                    <option value="no">No</option>
                  </select>
                </div>
                <div>
                  <label className={labelCls}>Stato occupazionale *</label>
                  <select value={form.occupazione} onChange={(e) => setF({ occupazione: e.target.value })} className={inputCls} data-testid="val-occupazione">
                    <option value="">Seleziona…</option>
                    {OCCUPANCY_OPTIONS.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className={labelCls}>Ultima ristrutturazione (anno)</label>
                  <input value={form.annoRistr} onChange={(e) => setF({ annoRistr: e.target.value })}
                    placeholder="Es. 2021 (opzionale)" inputMode="numeric" className={inputCls} data-testid="val-anno-ristr" />
                </div>
              </div>
            </FormSection>
          )}

          {/* Foto + contatti */}
          <FormSection n={form.tipoValutazione === "certificata" ? "07" : "06"} title="Foto e contatti">
            <p className="text-xs text-slate-600" data-testid="val-contact-intro">
              Inserisci i tuoi dati per ricevere il <b>Report Diagnostico Enea</b> e la pre-valutazione di idoneità al protocollo <b>Zero Vincoli</b>.
            </p>
            <div>
              <label className={labelCls}>Foto dell&apos;immobile (facoltative)</label>
              <div className="flex flex-wrap gap-1.5" data-testid="val-photos-grid">
                {photos.map((p) => (
                  <div key={p.file_id} className="relative w-14 h-14 rounded-md overflow-hidden border border-slate-200 group">
                    <img src={p.preview} alt="" className="w-full h-full object-cover" />
                    <button type="button" onClick={() => setPhotos((prev) => prev.filter((x) => x.file_id !== p.file_id))}
                      className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 flex items-center justify-center text-white transition"
                      data-testid="val-photo-remove">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
                {photos.length < MAX_FORM_PHOTOS && (
                  <button type="button" onClick={() => photosInputRef.current?.click()} disabled={uploadingPhotos}
                    className="w-14 h-14 rounded-md border-2 border-dashed border-[#A9D6E5] flex items-center justify-center text-[#14B8A6] hover:bg-[#A9D6E5]/15 disabled:opacity-50"
                    data-testid="val-photos-upload">
                    {uploadingPhotos ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImagePlus className="w-4 h-4" />}
                  </button>
                )}
              </div>
              <p className="mt-1 text-[10px] text-slate-500">Più foto carichi, più il perito potrà affinare la stima (max {MAX_FORM_PHOTOS}).</p>
              <input ref={photosInputRef} type="file" multiple accept="image/jpeg,image/jpg,image/png,image/webp" className="hidden"
                onChange={(e) => { handlePhotos(e.target.files); e.target.value = ""; }} data-testid="val-photos-input" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className={labelCls}>Email (per il report) *</label>
                <input type="email" value={form.email} onChange={(e) => setF({ email: e.target.value })}
                  placeholder="email@dominio.it" className={inputCls} data-testid="val-email" />
              </div>
              <div>
                <label className={labelCls}>Telefono</label>
                <input type="tel" value={form.telefono} onChange={(e) => setF({ telefono: e.target.value })}
                  placeholder="+39 … (opzionale)" className={inputCls} data-testid="val-telefono" />
              </div>
            </div>
          </FormSection>

          {formError && (
            <p className="flex items-start gap-1.5 text-sm text-red-600" data-testid="val-form-error">
              <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" /> {formError}
            </p>
          )}

          <button type="submit" disabled={computing || uploadingPhotos || uploadingDocs}
            className="w-full inline-flex items-center justify-center gap-2 px-4 py-3.5 rounded-lg bg-[#0b2545] text-white text-sm font-semibold hover:bg-[#13315c] disabled:opacity-60 shadow-sm"
            data-testid="val-submit">
            {(computing || uploadingPhotos || uploadingDocs) ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
            {uploadingPhotos || uploadingDocs ? "Attendi il caricamento delle foto…" : computing ? "Diagnostica in corso…" : "Genera il Report Diagnostico Enea"}
          </button>
          {computing && <DiagnosticProgress />}
          <p className="text-[10px] text-slate-400 text-center" data-testid="val-privacy-note">
            Inviando la richiesta dichiari di aver letto l'<a href="/privacy" target="_blank" rel="noreferrer" className="underline hover:text-[#14B8A6]">Informativa Privacy</a>. I tuoi dati sono usati solo per la valutazione richiesta.
          </p>
          <p className="text-[10px] text-slate-400 text-center">* campi obbligatori · Dati protetti, GDPR compliant · Quotazioni OMI ufficiali Agenzia delle Entrate</p>
        </form>
      )}

      {/* ===== RESULT ===== */}
      {phase === "result" && valuation && (
        <div className="p-4 sm:p-6 space-y-4 bg-slate-50/60" data-testid="valuation-result">
          <ValuationCard v={valuation} goal={form.goal} onDownload={form.tipoValutazione === "certificata" ? null : downloadPdf} teaser={form.tipoValutazione === "certificata"} />
          {form.tipoValutazione === "certificata" ? (
            <>
            <BrochureTeaser />
            <div className="rounded-xl border-2 border-[#14B8A6]/50 bg-white p-5" data-testid="val-premium-checkout">
              <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.18em] text-[#0d9488]">
                💎 Valutazione Premium ENEA
              </div>
              <div className="text-lg font-display font-semibold text-[#0b2545] mt-1.5">
                Sblocca la tua brochure Premium — <span className="text-[#0d9488]">€{premiumPrice}</span>
                {launchActive && <span className="ml-2 text-sm font-normal text-slate-400 line-through">€279</span>}
              </div>
              {launchActive ? (
                <div className="mt-2 flex items-center gap-2 rounded-lg bg-amber-50 border border-amber-300 px-3 py-2 text-xs font-semibold text-amber-800" data-testid="val-launch-offer">
                  🔥 Offerta lancio: rimasti {offer.remaining} posti su {offer.quota} a €{offer.price_eur} (poi €279)
                </div>
              ) : (
                <div className="mt-2 inline-flex items-center gap-2 rounded-lg bg-slate-50 border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-600" data-testid="val-standard-badge">
                  Prezzo standard: €279
                </div>
              )}
              <ul className="mt-3 space-y-1.5 text-xs text-slate-600">
                {[
                  "Brochure di oltre 10 pagine che spiega, fattore per fattore, perché il tuo immobile vale quello che vale",
                  "I tuoi 22 fattori Premium analizzati e tradotti in euro (incidono fino al 40% del valore)",
                  "Grafico dei prezzi OMI ufficiali della tua zona e calcolo 100% trasparente",
                  "Strategia di vendita: prezzo di uscita, valore obiettivo e minimo difendibile",
                  "Call di 30 minuti inclusa con un consulente Gruppo Vela — la prenoti tu online",
                ].map((t) => (
                  <li key={t} className="flex items-start gap-2">
                    <CheckCircle2 className="w-3.5 h-3.5 text-[#14B8A6] flex-shrink-0 mt-0.5" /> {t}
                  </li>
                ))}
              </ul>
              <div className="mt-4 rounded-lg border border-slate-200 bg-slate-50/70 p-3.5" data-testid="val-fiscal-box">
                <div className="text-[11px] font-bold uppercase tracking-[0.14em] text-[#0b2545]">🧾 Dati per la fattura (obbligatori)</div>
                <p className="text-[10px] text-slate-500 mt-0.5 mb-2.5">La fattura sarà emessa dall&apos;agenzia entro 12 giorni. Riceverai l&apos;email di pagamento come ricevuta.</p>
                <div className="space-y-2">
                  <div>
                    <input value={fiscal.name} onChange={(e) => { setFiscal((f) => ({ ...f, name: e.target.value })); setFiscalErr((er) => ({ ...er, name: null })); }}
                      placeholder="Nome e Cognome / Ragione Sociale *"
                      className={"w-full px-3 py-2 rounded-lg border bg-white text-sm text-[#0b2545] placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-[#14B8A6]/40 " + (fiscalErr.name ? "border-red-400 ring-2 ring-red-300/50" : "border-slate-200")}
                      data-testid="val-fiscal-name" />
                    {fiscalErr.name && <p className="text-[10px] text-red-600 mt-0.5" data-testid="val-fiscal-name-error">{fiscalErr.name}</p>}
                  </div>
                  <div>
                    <input value={fiscal.address} onChange={(e) => { setFiscal((f) => ({ ...f, address: e.target.value })); setFiscalErr((er) => ({ ...er, address: null })); }}
                      placeholder="Indirizzo di fatturazione (via, civico, CAP, città) *"
                      className={"w-full px-3 py-2 rounded-lg border bg-white text-sm text-[#0b2545] placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-[#14B8A6]/40 " + (fiscalErr.address ? "border-red-400 ring-2 ring-red-300/50" : "border-slate-200")}
                      data-testid="val-fiscal-address" />
                    {fiscalErr.address && <p className="text-[10px] text-red-600 mt-0.5" data-testid="val-fiscal-address-error">{fiscalErr.address}</p>}
                  </div>
                  <div>
                    <input value={fiscal.taxCode} onChange={(e) => { setFiscal((f) => ({ ...f, taxCode: e.target.value })); setFiscalErr((er) => ({ ...er, taxCode: null })); }}
                      placeholder="Codice Fiscale o Partita IVA *"
                      className={"w-full px-3 py-2 rounded-lg border bg-white text-sm text-[#0b2545] placeholder-slate-400 uppercase focus:outline-none focus:ring-2 focus:ring-[#14B8A6]/40 " + (fiscalErr.taxCode ? "border-red-400 ring-2 ring-red-300/50" : "border-slate-200")}
                      data-testid="val-fiscal-taxcode" />
                    {fiscalErr.taxCode && <p className="text-[10px] text-red-600 mt-0.5" data-testid="val-fiscal-taxcode-error">{fiscalErr.taxCode}</p>}
                  </div>
                </div>
              </div>
              <div className="mt-3 flex items-start gap-2.5 rounded-lg border border-[#14B8A6]/40 bg-[#14B8A6]/5 px-3.5 py-3" data-testid="val-visura-box">
                <input type="checkbox" id="visura-check" checked={visuraReq} onChange={(e) => setVisuraReq(e.target.checked)}
                  className="mt-0.5 w-4 h-4 accent-[#14B8A6] cursor-pointer" data-testid="val-visura-checkbox" />
                <label htmlFor="visura-check" className="text-xs text-slate-700 cursor-pointer select-none">
                  <b>📄 Non ho la visura catastale: richiedetela voi per me (+€5)</b>
                  <span className="block text-[10px] text-slate-500 mt-0.5">
                    La visura serve per il valore preciso MCM. La richiediamo noi all&apos;Agenzia delle Entrate: dopo il pagamento ti chiederemo via email una copia della carta d&apos;identità dell&apos;intestatario.
                  </span>
                </label>
              </div>
              <div className="mt-3 flex items-center justify-between rounded-lg bg-[#0b2545] px-4 py-2.5" data-testid="val-total-row">
                <span className="text-xs text-[#A9D6E5]">Totale da pagare {visuraReq ? `(Premium €${premiumPrice} + Visura €5)` : "(IVA inclusa)"}</span>
                <span className="text-lg font-bold text-white" data-testid="val-total-amount">€{totalAmount}</span>
              </div>
              <div className="flex flex-wrap gap-2 mt-4">
                <button type="button" onClick={payPremium} disabled={paying}
                  className="inline-flex items-center gap-2 px-5 py-3 rounded-lg bg-[#0070BA] text-white text-sm font-semibold hover:bg-[#005ea6] disabled:opacity-60 shadow-md transition"
                  data-testid="val-pay-premium">
                  {paying ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
                  {paying ? "Reindirizzamento a PayPal…" : `Paga €${totalAmount} con PayPal`}
                </button>
                <button type="button" onClick={() => setPhase("form")} disabled={paying}
                  className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-white border border-slate-300 text-[#0b2545] text-sm font-medium hover:border-[#14B8A6]"
                  data-testid="val-edit-data">
                  Modifica i dati
                </button>
              </div>
              <p className="mt-3 text-[10px] text-slate-400">
                Conto PayPal o carta — pagamento gestito da <b>PayPal</b>, i dati di pagamento non passano mai dai nostri server.
                La brochure arriva via email subito dopo il pagamento e il team Gruppo Vela prende in carico la pratica.
              </p>
            </div>
            </>
          ) : null}
        </div>
      )}

      {/* ===== DONE ===== */}
      {phase === "done" && (
        <div className="p-4 sm:p-6 space-y-4 bg-slate-50/60" data-testid="val-success">
          {(form.intenzione === "vendo_subito" || form.intenzione === "entro_3_mesi") && (
            <div className="rounded-xl border-2 border-amber-400 bg-amber-50 p-4" data-testid="val-hot-banner">
              <div className="flex items-center gap-2 text-sm font-bold text-amber-800">
                <Flame className="w-4 h-4 flex-shrink-0" /> Percorso prioritario attivato
              </div>
              <p className="mt-1 text-xs text-amber-900 leading-relaxed">
                Hai dichiarato di voler vendere {form.intenzione === "vendo_subito" ? "subito" : "entro 3 mesi"}: la tua pratica
                passa in cima alla coda. Un consulente Gruppo Vela ti contatta <b>entro poche ore</b> per la validazione Zero Vincoli.
              </p>
            </div>
          )}
          {valuation && <ValuationCard v={valuation} goal={form.goal} onDownload={downloadPdf} />}
          {(form.intenzione === "vendo_subito" || form.intenzione === "entro_3_mesi") && valuation?.estimated_value_median && (
            <div className="rounded-xl border border-[#0b2545]/20 bg-white p-4" data-testid="val-hot-draft">
              <div className="text-[10px] font-bold uppercase tracking-[0.16em] text-[#0d9488] mb-2">Bozza di valutazione accurata — anteprima Professional</div>
              <div className="space-y-1.5 text-xs">
                <div className="flex justify-between gap-3">
                  <span className="text-slate-600">Valore puntuale stimato (bozza)</span>
                  <span className="font-bold text-[#0b2545]" data-testid="val-hot-median">{fmtEur(valuation.estimated_value_median)}</span>
                </div>
                <div className="flex justify-between gap-3">
                  <span className="text-slate-600">€/mq effettivo dopo premi e malus</span>
                  <span className="font-bold text-[#0b2545]">{fmtEur(valuation.breakdown?.effective_price_sqm)}</span>
                </div>
                {["Prezzo di uscita consigliato (per non bruciare l'annuncio)", "Valore minimo difendibile in trattativa", "Strategia di vendita 90 giorni, passo per passo"].map((k) => (
                  <div key={k} className="flex justify-between items-center gap-3">
                    <span className="text-slate-500">{k}</span>
                    <span className="font-mono text-[11px] bg-slate-100 rounded px-2 py-0.5 select-none blur-[3px] flex-shrink-0">€ •••.•••</span>
                  </div>
                ))}
              </div>
              <p className="mt-2.5 text-[11px] text-slate-600">🔒 I campi oscurati si sbloccano con la <b>Valutazione Professional</b>: il passo necessario per capire se puoi davvero vendere nei tempi che hai dichiarato.</p>
              <a href="/valutazione?premium=1"
                className="mt-2 inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#0b2545] text-white text-xs font-semibold hover:bg-[#13315c] transition"
                data-testid="val-hot-premium-cta">
                Sblocca la Valutazione Professional
              </a>
            </div>
          )}
          <div className="flex items-start gap-3 p-4 rounded-xl bg-[#14B8A6]/10 border border-[#14B8A6]/30">
            <CheckCircle2 className="w-6 h-6 text-[#14B8A6] flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <div className="text-sm font-semibold text-[#0b2545]">
                {firstName ? `${firstName}, il tuo dossier è protocollato.` : "Dossier protocollato."}
              </div>
              <div className="text-xs text-slate-600 mt-0.5">
                Codice pratica <span className="font-mono font-semibold text-[#0b2545]" data-testid="val-protocol-code">{protocolCode}</span> · presa in carico immediata.
              </div>
            </div>
          </div>

          <div className="rounded-xl border border-slate-200 bg-white p-4">
            <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-[#14B8A6] mb-3">Cosa succede ora</div>
            <ol className="space-y-3">
              {[
                { done: true, t: "Dossier ricevuto e protocollato", s: "Dati, documenti e foto acquisiti dal sistema MCM." },
                { active: true, t: "Analisi tecnica del perito", s: "Un valutatore Gruppo Vela verifica i comparabili reali della tua micro-zona." },
                { t: "Valutazione definitiva + strategia di scambio", s: "La ricevi via email entro 24h lavorative, con il report completo." },
              ].map((p, idx) => (
                <li key={idx} className="flex items-start gap-3">
                  <span className={`mt-0.5 w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 ${p.done ? "bg-[#14B8A6] text-white" : p.active ? "bg-[#0b2545] text-white" : "bg-slate-200 text-slate-500"}`}>
                    {p.done ? <CheckCircle2 className="w-3.5 h-3.5" /> : p.active ? <Loader2 className="w-3 h-3 animate-spin" /> : <span className="text-[10px] font-bold">{idx + 1}</span>}
                  </span>
                  <div>
                    <div className={`text-xs font-semibold ${p.active ? "text-[#0b2545]" : "text-slate-700"}`}>{p.t}</div>
                    <div className="text-[11px] text-slate-500 leading-snug">{p.s}</div>
                  </div>
                </li>
              ))}
            </ol>
          </div>

          <div className="grid grid-cols-3 gap-2">
            {[
              { icon: ShieldCheck, v: "±0,8%", l: "scarto stima/rogito" },
              { icon: TrendingUp, v: "1.482", l: "comparabili attivi" },
              { icon: Compass, v: "94 gg", l: "tempo medio vendita" },
            ].map((k, i) => (
              <div key={i} className="rounded-lg bg-[#0b2545] text-white px-2.5 py-3 text-center">
                <k.icon className="w-4 h-4 mx-auto text-[#A9D6E5] mb-1" />
                <div className="text-sm font-bold leading-none">{k.v}</div>
                <div className="text-[9px] text-[#A9D6E5] mt-1 leading-tight">{k.l}</div>
              </div>
            ))}
          </div>

          <div className="flex flex-wrap gap-2">
            {valuation?.estimated_value_median && (
              <button type="button" onClick={downloadPdf}
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-[#14B8A6] text-white text-sm font-semibold hover:bg-[#0d9488] shadow-sm transition"
                data-testid="val-download-pdf">
                <Download className="w-4 h-4" /> Scarica il report preliminare
              </button>
            )}
            <button type="button" onClick={shareWhatsApp}
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-[#25D366] text-white text-sm font-semibold hover:bg-[#1ebe5b] shadow-sm transition"
              data-testid="val-share-whatsapp">
              <svg viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor" aria-hidden="true"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.52.149-.174.198-.298.297-.497.1-.198.05-.371-.025-.52-.074-.149-.668-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
              Condividi su WhatsApp
            </button>
          </div>
          <p className="text-[11px] text-slate-500" data-testid="val-share-hint">📣 Conosci amici o parenti proprietari di casa? Mandagli la tua valutazione su WhatsApp: la provano gratis in 2 minuti.</p>

          {/* Call booking */}
          <div className="rounded-xl border border-[#14B8A6]/30 bg-[#14B8A6]/[0.06] p-4" data-testid="val-call-card">
            {!callDone ? (
              <>
                <div className="flex items-start gap-3">
                  <span className="w-9 h-9 rounded-full bg-[#14B8A6] text-white flex items-center justify-center flex-shrink-0">
                    <Phone className="w-4 h-4" />
                  </span>
                  <div className="flex-1">
                    <div className="text-sm font-semibold text-[#0b2545]">Preferisci parlarne subito?</div>
                    <div className="text-xs text-slate-600">Prenota una call di 15 minuti con un tecnico Gruppo Vela.</div>
                  </div>
                  {!callOpen && (
                    <button type="button" onClick={() => setCallOpen(true)}
                      className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-[#0b2545] text-white text-xs font-semibold hover:bg-[#13315c] flex-shrink-0"
                      data-testid="val-call-open">
                      <CalendarClock className="w-3.5 h-3.5" /> Prenota
                    </button>
                  )}
                </div>
                {callOpen && (
                  <div className="mt-4 space-y-3" data-testid="val-call-scheduler">
                    <div>
                      <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500 mb-1.5">Giorno preferito</div>
                      <div className="flex flex-wrap gap-2">
                        {nextWorkingDays(5).map((d, i) => (
                          <button key={d.value} type="button" onClick={() => setCallDate(d.value)}
                            className={`px-3 py-2 rounded-lg text-xs font-medium border transition ${callDate === d.value ? "bg-[#0b2545] text-white border-[#0b2545]" : "bg-white text-[#0b2545] border-slate-200 hover:border-[#14B8A6]"}`}
                            data-testid={`val-call-day-${i}`}>
                            {d.label}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div>
                      <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500 mb-1.5">Fascia oraria</div>
                      <div className="flex flex-wrap gap-2">
                        {CALL_SLOTS.map((s, i) => (
                          <button key={s} type="button" onClick={() => setCallSlot(s)}
                            className={`px-3 py-2 rounded-lg text-xs font-medium border transition ${callSlot === s ? "bg-[#14B8A6] text-white border-[#14B8A6]" : "bg-white text-[#0b2545] border-slate-200 hover:border-[#14B8A6]"}`}
                            data-testid={`val-call-slot-${i}`}>
                            {s}
                          </button>
                        ))}
                      </div>
                    </div>
                    <button type="button" onClick={submitCallRequest}
                      disabled={callSubmitting || !callDate || !callSlot}
                      className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-[#0b2545] text-white text-sm font-semibold hover:bg-[#13315c] disabled:opacity-50 transition"
                      data-testid="val-call-submit">
                      {callSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <CalendarClock className="w-4 h-4" />}
                      Conferma prenotazione
                    </button>
                  </div>
                )}
              </>
            ) : (
              <div className="flex items-start gap-3" data-testid="val-call-success">
                <CheckCircle2 className="w-6 h-6 text-[#14B8A6] flex-shrink-0 mt-0.5" />
                <div>
                  <div className="text-sm font-semibold text-[#0b2545]">Richiesta di call inviata!</div>
                  <div className="text-xs text-slate-600">Confermeremo lo slot <b>{callDate} · {callSlot}</b> via email o telefono a breve.</div>
                </div>
              </div>
            )}
          </div>

          {/* Beta feedback */}
          {beta && (
            <div className="rounded-xl border border-amber-300/60 bg-amber-50/60 p-4" data-testid="val-beta-feedback">
              {!feedbackDone ? (
                <>
                  <div className="text-sm font-semibold text-[#0b2545]">🧪 Beta test — 3 domande veloci</div>
                  <div className="mt-3 space-y-3">
                    <div>
                      <div className="text-xs text-slate-700 mb-1">È stato facile inserire i dati?</div>
                      <StarRating value={easeRating} onChange={setEaseRating} testId="beta-ease-rating" />
                    </div>
                    <div>
                      <div className="text-xs text-slate-700 mb-1">Ti fidi della valutazione ricevuta?</div>
                      <StarRating value={trustRating} onChange={setTrustRating} testId="beta-trust-rating" />
                    </div>
                    <div>
                      <div className="text-xs text-slate-700 mb-1">Cosa cambieresti o non ti è piaciuto? <span className="text-slate-400">(opzionale)</span></div>
                      <textarea value={feedbackText} onChange={(e) => setFeedbackText(e.target.value)} rows={2}
                        placeholder="Scrivi qui…"
                        className="w-full px-3 py-2 rounded-lg border border-slate-200 bg-white text-sm text-[#0b2545] placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-[#14B8A6]/40 resize-none"
                        data-testid="beta-feedback-text" />
                    </div>
                    <button type="button" onClick={submitBetaFeedback} disabled={feedbackSubmitting}
                      className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#14B8A6] text-white text-xs font-semibold hover:bg-[#0d9488] disabled:opacity-60"
                      data-testid="beta-feedback-submit">
                      {feedbackSubmitting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                      Invia feedback
                    </button>
                  </div>
                </>
              ) : (
                <div className="flex items-start gap-2.5" data-testid="beta-feedback-success">
                  <CheckCircle2 className="w-5 h-5 text-[#14B8A6] flex-shrink-0 mt-0.5" />
                  <div className="text-xs text-slate-700">Feedback inviato — grazie, ci aiuti a migliorare! 🙏</div>
                </div>
              )}
            </div>
          )}

          {/* Upsell */}
          {form.tipoValutazione !== "certificata" && (
            <div className="rounded-xl border border-[#14B8A6]/30 bg-white p-4" data-testid="val-upsell-card">
              {!upgradeDone ? (
                <>
                  <div className="text-sm font-semibold text-[#0b2545]">Vuoi il valore preciso del tuo immobile?</div>
                  <p className="text-xs text-slate-600 mt-1">
                    Con la <b>Valutazione Certificata (€{premiumPrice}{launchActive ? " in offerta lancio, invece di €279" : ""})</b> ricevi: valore preciso con metodo MCM completo, indicazioni per vendere in autonomia al massimo prezzo e una call di 30 minuti con un consulente Gruppo Vela. Bastano le foto dell&apos;immobile e le planimetrie catastali (o la visura — se non la hai, la richiediamo noi per te a €5 con la tua carta d&apos;identità).
                  </p>
                  <div className="flex flex-wrap gap-2 mt-3">
                    <button type="button" onClick={() => requestUpgrade("premium")} disabled={upgradeSubmitting}
                      className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg bg-[#0b2545] text-white text-xs font-semibold hover:bg-[#13315c] disabled:opacity-60"
                      data-testid="val-upgrade-premium">
                      {upgradeSubmitting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "⭐"} Richiedi la Certificata — €{premiumPrice}
                    </button>
                  </div>
                </>
              ) : (
                <div className="flex items-start gap-2.5" data-testid="val-upgrade-success">
                  <CheckCircle2 className="w-5 h-5 text-[#14B8A6] flex-shrink-0 mt-0.5" />
                  <div className="text-xs text-slate-700">
                    Richiesta <b>Valutazione Certificata</b> inviata! Un consulente Gruppo Vela ti ricontatterà per concordare i dettagli e fissare la call di 30 minuti.
                  </div>
                </div>
              )}
            </div>
          )}

          <button type="button" onClick={restart}
            className="inline-flex items-center gap-2 text-sm text-[#2c7da0] hover:text-[#14B8A6] font-medium"
            data-testid="val-new-valuation">
            <RotateCcw className="w-4 h-4" /> Nuova valutazione
          </button>
        </div>
      )}

      {/* Footer note */}
      <div className="px-5 py-2.5 bg-slate-50/70 border-t border-slate-100 flex items-center justify-between text-[11px] text-slate-500">
        <span className="flex items-center gap-1.5"><Sparkles className="w-3 h-3 text-[#14B8A6]" /> Powered by AI · Gruppo Vela</span>
        <span className="hidden sm:inline">Dati protetti · GDPR compliant</span>
      </div>
    </div>
  );
}

function FormSection({ n, title, children }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 space-y-4 shadow-sm">
      <div className="flex items-center gap-2.5">
        <span className="w-7 h-7 rounded-full bg-[#0b2545] text-[#A9D6E5] text-[10px] font-semibold flex items-center justify-center flex-shrink-0">{n}</span>
        <span className="text-sm font-display font-semibold text-[#0b2545]">{title}</span>
      </div>
      {children}
    </div>
  );
}

function BrochureTeaser() {
  const teaserRows = [
    ["Classe energetica APE", "+5%"], ["Riscaldamento autonomo", "+2%"], ["Doppi servizi", "+2%"],
    ["Esposizione doppia", "+2%"], ["Vista panoramica", "+3%"], ["Infissi triplo vetro", "+2%"],
    ["Cantina / soffitta", "+1%"], ["Climatizzazione", "+1%"],
  ];
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="val-brochure-teaser">
      <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#0d9488] mb-3">👀 Anteprima della tua brochure Premium</div>
      <div className="relative">
        <div className="grid grid-cols-2 gap-3 max-w-md mx-auto">
          <div className="relative rounded-lg overflow-hidden border border-slate-200 aspect-[3/4]">
            <div className="absolute inset-0 blur-[4px] select-none pointer-events-none bg-gradient-to-br from-[#13315c] to-[#0b2545] p-4 flex flex-col">
              <div className="text-[7px] uppercase tracking-widest text-[#A9D6E5]">Gruppo Vela · Copilot AI</div>
              <div className="mt-3 text-lg font-display font-semibold text-white leading-tight">VALUTAZIONE PREMIUM</div>
              <div className="text-[8px] font-bold tracking-widest text-[#14B8A6] mt-1">ENEA SPECIALIST</div>
              <div className="mt-auto space-y-1">
                {["11 pagine · 22 fattori", "Grafico OMI di zona", "Calcolo in euro", "Strategia di vendita"].map((x) => (
                  <div key={x} className="text-[8px] text-white/80">✓ {x}</div>
                ))}
              </div>
            </div>
          </div>
          <div className="relative rounded-lg overflow-hidden border border-slate-200 aspect-[3/4] bg-white">
            <div className="absolute inset-0 blur-[4px] select-none pointer-events-none p-3">
              <div className="text-[8px] font-bold text-[#0b2545]">I 22 fattori del tuo immobile</div>
              {teaserRows.map(([k, pct]) => (
                <div key={k} className="flex justify-between text-[7px] text-slate-600 border-b border-slate-100 py-1">
                  <span>{k}</span><span className="font-bold text-[#0d9488]">{pct} · +€ •.•••</span>
                </div>
              ))}
              <div className="mt-2 h-10 bg-gradient-to-r from-[#14B8A6]/30 to-[#0b2545]/30 rounded" />
              <div className="mt-1 text-[7px] text-slate-500">Quotazioni OMI · Agenzia delle Entrate</div>
            </div>
          </div>
        </div>
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="bg-white/95 border border-slate-200 shadow-lg rounded-xl px-5 py-3 text-center" data-testid="val-teaser-unlock-badge">
            <div className="text-xl">🔒</div>
            <div className="text-xs font-bold text-[#0b2545]">Brochure completa di 10+ pagine</div>
            <div className="text-[10px] text-slate-500">Si sblocca subito dopo il pagamento — arriva via email</div>
          </div>
        </div>
      </div>
    </div>
  );
}

function DiagnosticProgress() {
  const [i, setI] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setI((x) => Math.min(x + 1, DIAG_STEPS.length - 1)), 1600);
    return () => clearInterval(t);
  }, []);
  return (
    <div className="rounded-xl bg-[#0b2545] px-4 py-3 flex items-center gap-3" data-testid="val-diagnostic-progress">
      <Loader2 className="w-4 h-4 text-[#14B8A6] animate-spin flex-shrink-0" />
      <span className="text-xs text-white font-medium">{DIAG_STEPS[i]}</span>
    </div>
  );
}

function ValuationCard({ v, onDownload, teaser, goal }) {
  if (!v) return null;
  const fmt = (n) => (n === null || n === undefined) ? "—" : new Intl.NumberFormat("it-IT").format(n);
  const bd = v.breakdown || {};
  const rk = v.risk || {};
  return (
    <div className="w-full bg-white rounded-2xl border border-[#14B8A6]/30 shadow-md overflow-hidden" data-testid="val-valuation-card">
      <div className="px-5 py-3 bg-gradient-to-r from-[#0b2545] to-[#14B8A6] text-white">
        <div className="text-[10px] uppercase tracking-[0.2em] text-[#A9D6E5]">Diagnostica Enea Completata</div>
        <div className="text-sm font-semibold">{v.zone}</div>
        {v.price_source && (
          <div className="mt-0.5 text-[10px] text-white/85" data-testid="val-price-source">Fonte €/mq: {v.price_source}</div>
        )}
        {(v.price_source || "").includes("OMI") && (
          <div className="mt-1.5 inline-flex items-center gap-1.5 text-[11px] font-semibold text-[#0b2545] bg-white rounded-full px-2.5 py-1 shadow-sm" data-testid="omi-source-badge">
            <ShieldCheck className="w-3.5 h-3.5 text-[#14B8A6]" /> Fonte: OMI · Agenzia delle Entrate
          </div>
        )}
        {v.property_type && (
          <div className="mt-1 ml-1 inline-flex items-center gap-1.5 text-[11px] font-medium text-white/90 bg-white/15 rounded-full px-2.5 py-0.5" data-testid="val-property-type">
            {v.property_type}
          </div>
        )}
      </div>
      <div className="px-5 py-4 space-y-3">
        {v.estimated_value_median ? (
          <>
            <div>
              <div className="text-[11px] uppercase tracking-[0.14em] text-slate-500">{teaser ? "Forbice di Vendibilità — anteprima parziale" : "Forbice di Vendibilità Immediata"}</div>
              <div className="text-2xl font-display font-semibold text-[#0b2545]">
                € {fmt(v.estimated_value_low)} – € {fmt(v.estimated_value_high)}
              </div>
              {!teaser && (
                <p className="text-[11px] text-slate-600 mt-1 leading-relaxed" data-testid="val-harsh-note">
                  Questo non è il prezzo che vorresti: è il prezzo che il mercato è disposto a pagare <b>oggi</b>.
                  Prezzare l'immobile sopra questa forbice ti espone al “Turismo Immobiliare” e azzera le
                  possibilità di attirare acquirenti con liquidità pronta.
                </p>
              )}
              {teaser && (
                <div className="text-[10px] font-semibold text-[#0d9488] mt-0.5" data-testid="val-teaser-note">
                  🔒 Il valore preciso e il calcolo completo in euro si sbloccano con la brochure Premium
                </div>
              )}
              {v.range_source === "omi_min_max" && (
                <div className="text-[10px] text-slate-500 mt-0.5" data-testid="val-range-source">
                  Range calcolato sulle quotazioni OMI minime e massime reali della zona
                </div>
              )}
            </div>
            {!teaser && (
              <div className="rounded-lg bg-[#0b2545] px-3.5 py-3 text-xs text-white leading-relaxed" data-testid="val-risk-block">
                <div className="text-[10px] font-bold uppercase tracking-[0.16em] text-[#F59E0B] mb-1.5">Rischio svalutazione — vendita tradizionale nella tua zona</div>
                <ul className="space-y-1">
                  <li>• Tempo stimato di vendita: <b data-testid="val-risk-months">{rk.months_min ? `${rk.months_min}–${rk.months_max} mesi` : "6–8 mesi"}</b></li>
                  <li>• Rischio di incappare in proposte vincolate al mutuo: <b data-testid="val-risk-vincolate">{rk.vincolate_pct ? `${rk.vincolate_pct}%` : "85%"}</b></li>
                  <li>• Rischio di dover abbassare il prezzo del 10% dopo i primi 90 giorni: <b data-testid="val-risk-ribasso">{rk.ribasso_label || "altissimo"}</b></li>
                </ul>
                {rk.note && (
                  <div className="mt-1.5 text-[10px] text-white/60" data-testid="val-risk-note">{rk.note}</div>
                )}
              </div>
            )}
            {v.trend?.series?.length >= 3 && (() => {
              const ser = v.trend.series;
              const vals = ser.map((p) => p.price_sqm);
              const mn = Math.min(...vals), mx = Math.max(...vals), rg = (mx - mn) || 1;
              const px = (i) => (i / (ser.length - 1)) * 264 + 8;
              const py = (val) => 50 - ((val - mn) / rg) * 36;
              const pts = ser.map((p, i) => `${px(i)},${py(p.price_sqm)}`).join(" ");
              const up = v.trend.delta_pct >= 0;
              return (
                <div className="rounded-lg border border-slate-200 bg-white px-3.5 py-3" data-testid="val-trend-block">
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <span className="text-[10px] font-bold uppercase tracking-[0.16em] text-[#0b2545]">Andamento €/mq zona {v.trend.from_year}–{v.trend.to_year}</span>
                    <span className={`text-[11px] font-bold ${up ? "text-emerald-600" : "text-red-600"}`} data-testid="val-trend-delta">{up ? "+" : ""}{String(v.trend.delta_pct).replace(".", ",")}%</span>
                  </div>
                  <svg viewBox="0 0 280 56" className="w-full h-14" preserveAspectRatio="none" aria-hidden="true">
                    <polyline points={pts} fill="none" stroke="#14B8A6" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
                    {ser.map((p, i) => (
                      <circle key={p.year} cx={px(i)} cy={py(p.price_sqm)} r="2.4" fill="#14B8A6" />
                    ))}
                  </svg>
                  <div className="flex justify-between text-[9px] text-slate-500">
                    <span>{v.trend.from_year} · € {fmt(ser[0].price_sqm)}/mq</span>
                    <span>{v.trend.to_year} · € {fmt(ser[ser.length - 1].price_sqm)}/mq</span>
                  </div>
                  <div className="mt-1 text-[9px] text-slate-400">{v.trend.note}</div>
                </div>
              );
            })()}

            {v.usufrutto?.nuda_median && (
              <div className="rounded-lg border border-[#0d9488]/30 bg-[#f0fdfa] px-3.5 py-3 text-xs" data-testid="val-usufrutto-block">
                <div className="text-[10px] font-bold uppercase tracking-[0.16em] text-[#0d9488] mb-1.5">Nuda proprietà — usufruttuario di {v.usufrutto.age} anni</div>
                <div className="flex justify-between gap-3">
                  <span className="text-slate-600">Valore nuda proprietà</span>
                  <b className="text-[#0b2545]" data-testid="val-usufrutto-nuda">€ {fmt(v.usufrutto.nuda_low)} – {fmt(v.usufrutto.nuda_high)}</b>
                </div>
                <div className="flex justify-between gap-3 mt-1">
                  <span className="text-slate-600">Coefficiente usufrutto</span>
                  <b className="text-[#0b2545]">{v.usufrutto.usufruct_pct}% <span className="font-normal text-slate-500">(nuda {v.usufrutto.nuda_pct}%)</span></b>
                </div>
                <div className="flex justify-between gap-3 mt-1">
                  <span className="text-slate-600">Valore dell&apos;usufrutto (mediana)</span>
                  <b className="text-[#0b2545]">€ {fmt(v.usufrutto.usufruct_value_median)}</b>
                </div>
                <div className="mt-1.5 text-[10px] text-slate-500">{v.usufrutto.note}</div>
              </div>
            )}
            {!teaser && (
              <div className="rounded-lg border-2 border-[#14B8A6] bg-[#14B8A6]/[0.07] px-3.5 py-3" data-testid="val-idoneita-block">
                <div className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.12em] text-[#0d9488]">
                  <ShieldCheck className="w-4 h-4 flex-shrink-0" /> Stato immobile: IDONEO per il protocollo Zero Vincoli
                </div>
                <p className="mt-1.5 text-xs text-slate-700 leading-relaxed">
                  La tua casa ha le caratteristiche per essere assorbita dal nostro database di acquirenti altospendenti
                  e certificati. Noi <b>NON</b> raccogliamo proposte vincolate al mutuo: se vendi con noi, la tua casa
                  esce dal mercato solo per proposte certe e non condizionate.
                </p>
              </div>
            )}
            {!teaser && goal === "scambio_casa" && (
              <div className="rounded-lg border border-amber-300 bg-amber-50 px-3.5 py-3 text-xs text-amber-900 leading-relaxed" data-testid="val-scambio-block">
                <b>Hai dichiarato di dover ricomprare: questo è il momento più pericoloso.</b> Se la banca del tuo
                acquirente rifiuta il mutuo, perdi anche la casa nuova che hai bloccato. Con il protocollo Zero Vincoli
                i capitali in entrata sono garantiti: sincronizziamo i tuoi rogiti senza che tu debba mai rischiare
                i tuoi risparmi o restare per strada.
              </div>
            )}
            {!teaser && (
              <div data-testid="val-validation-cta">
                <button
                  type="button"
                  onClick={() => document.querySelector('[data-testid="val-call-card"]')?.scrollIntoView({ behavior: "smooth", block: "center" })}
                  className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-[#F59E0B] text-[#06222b] font-bold text-sm hover:bg-[#fbbf24] transition-colors"
                  data-testid="val-risk-cta"
                >
                  Richiedi la Validazione per il Protocollo Zero Vincoli →
                </button>
                <p className="mt-1.5 text-[10px] text-slate-500 text-center">
                  Cliccando confermi di voler essere contattato da un nostro consulente per scartare i perditempo e vendere solo ad acquirenti garantiti.
                </p>
              </div>
            )}
            <div className="grid grid-cols-2 gap-3 pt-2 border-t border-slate-100">
              <div>
                <div className="text-[11px] uppercase tracking-[0.14em] text-slate-500">€/mq</div>
                <div className="text-sm font-semibold text-[#0b2545]">€ {fmt(v.price_sqm_low)} – € {fmt(v.price_sqm_high)}</div>
              </div>
              <div>
                <div className="text-[11px] uppercase tracking-[0.14em] text-slate-500">Confidenza</div>
                <div className="text-sm font-semibold text-[#14B8A6]">{v.confidence_label || v.confidence}</div>
              </div>
            </div>
            {v.zone_comparison && (
              <div className="mt-2 rounded-lg bg-[#F0F9FC] border border-[#A9D6E5]/50 px-3 py-2" data-testid="val-zone-comparison">
                <div className="text-[11px] uppercase tracking-[0.14em] text-slate-500">Confronto con la zona ({v.zone_comparison.source})</div>
                <div className="text-sm text-[#0b2545] mt-0.5">
                  La tua casa: <b>€ {fmt(v.zone_comparison.property_price_sqm)}/mq</b> · Media zona: <b>€ {fmt(v.zone_comparison.zone_avg_price_sqm)}/mq</b> — <span className={"font-semibold " + (v.zone_comparison.delta_pct > 3 ? "text-[#0d9488]" : v.zone_comparison.delta_pct < -3 ? "text-amber-600" : "text-slate-600")}>{v.zone_comparison.label}</span>
                </div>
              </div>
            )}
            {(bd.features || []).some((f) => (f.id || "").startsWith("taglio_") && f.pct > 0) && (
              <div className="mt-2 rounded-lg bg-gradient-to-r from-[#0b2545] to-[#13315c] px-3 py-2.5 flex items-start gap-2" data-testid="val-taglio-badge">
                <Flame className="w-4 h-4 text-[#F59E0B] flex-shrink-0 mt-0.5" />
                <div className="text-xs text-white">
                  <b className="text-[#F59E0B]">I tagli piccoli sono i più richiesti in zona:</b> mono, bilo e trilocali si vendono più in fretta e a €/mq più alti. Con la <b>valutazione Premium</b> scopri il prezzo esatto a cui puoi chiudere la vendita.
                </div>
              </div>
            )}
            <div className="pt-2 border-t border-slate-100 text-xs text-slate-600 space-y-0.5" data-testid="val-breakdown">
              <div className="flex justify-between"><span>Superficie commerciale</span><span className="font-medium text-[#0b2545]">{fmt(bd.base_sqm)} mq</span></div>
              {bd.garden_counted && (
                <div className="flex justify-between text-[#14B8A6]"><span>+ Giardino ({fmt(bd.garden_sqm)} mq ÷ 10)</span><span className="font-medium">+{fmt(bd.garden_equivalent_sqm)} mq</span></div>
              )}
              {bd.terrazzo_counted && (
                <div className="flex justify-between text-[#14B8A6]"><span>+ Terrazzo ({fmt(bd.terrazzo_sqm)} mq ÷ 10)</span><span className="font-medium">+{fmt(bd.terrazzo_equivalent_sqm)} mq</span></div>
              )}
              <div className="flex justify-between pt-1 border-t border-slate-100"><span className="font-semibold text-[#0b2545]">Superficie valutata</span><span className="font-semibold text-[#0b2545]">{fmt(bd.total_sqm)} mq</span></div>
              {v.stato_conservativo && (
                <div className="flex justify-between pt-1"><span>Stato conservativo</span><span className="font-medium text-[#0b2545]">{v.stato_conservativo}</span></div>
              )}
              {bd.floor_label && (
                <div className="flex justify-between"><span>Piano</span><span className="font-medium text-[#0b2545]">{bd.floor_label}</span></div>
              )}
            </div>
            {(bd.features || []).length > 0 && (() => {
              const featBase = v.estimated_value_median ? (v.estimated_value_median - (bd.garage_lump?.value_mid || 0)) / (1 + (bd.features_pct || 0)) : null;
              return (
                <div className="relative">
                  <div className={`pt-2 border-t border-slate-100 text-xs space-y-1 ${teaser ? "blur-[5px] select-none pointer-events-none" : ""}`} data-testid="val-features-breakdown">
                  <div className="text-[11px] uppercase tracking-[0.14em] text-slate-500 mb-1">Premi / malus applicati</div>
                  {bd.features.map((f) => {
                    const eur = featBase ? Math.round(f.pct * featBase) : null;
                    return (
                      <div key={f.id || f.label} className={`flex justify-between items-center ${f.pct >= 0 ? "text-[#0d9488]" : "text-red-600"}`}>
                        <span>{f.label}</span>
                        <span className="font-semibold inline-flex items-center">
                          {f.pct >= 0 ? "+" : ""}{Math.round(f.pct * 100)}%
                          {eur !== null && (
                            <span className={`ml-1.5 px-1.5 py-0.5 rounded-full text-[11px] font-bold ${f.pct >= 0 ? "bg-[#14B8A6]/10 text-[#0d9488]" : "bg-red-50 text-red-600"}`} data-testid="val-feature-eur">
                              {eur >= 0 ? "+" : "−"}€ {fmt(Math.abs(eur))}
                            </span>
                          )}
                        </span>
                      </div>
                    );
                  })}
                  {featBase && (
                    <p className="text-[10px] text-slate-400 pt-0.5">Valore in € calcolato sul valore mediano stimato del tuo immobile.</p>
                  )}
                  </div>
                  {teaser && (
                    <div className="absolute inset-0 flex flex-col items-center justify-center gap-1" data-testid="val-teaser-lock">
                      <span className="text-2xl">🔒</span>
                      <span className="text-[11px] font-bold text-[#0b2545] bg-white/90 border border-slate-200 rounded-full px-3 py-1 shadow">
                        Premi e malus in € — si sbloccano con la Premium
                      </span>
                    </div>
                  )}
                </div>
              );
            })()}
            {bd.garage_lump && (
              <div className="flex justify-between items-center gap-2 pt-2 border-t border-slate-100 text-xs" data-testid="val-garage-lump">
                <span className="text-slate-600">{bd.garage_lump.label}</span>
                <span className="font-semibold text-[#0d9488] whitespace-nowrap">+€ {fmt(bd.garage_lump.value_low)} – € {fmt(bd.garage_lump.value_high)}</span>
              </div>
            )}
            {(v.comparables || []).length > 0 && (
              <div className="pt-2 border-t border-slate-100" data-testid="val-comparables">
                <div className="text-[11px] uppercase tracking-[0.14em] text-slate-500 mb-1.5">Immobili simili in zona (somiglianza ≥ 80%)</div>
                <div className="space-y-1.5">
                  {v.comparables.slice(0, 3).map((c, i) => (
                    <div key={i} className="flex items-center justify-between gap-2 rounded-lg bg-slate-50 border border-slate-100 px-2.5 py-1.5" data-testid={`val-comparable-${i}`}>
                      <div className="min-w-0">
                        <div className="text-xs font-medium text-[#0b2545] truncate">{c.label}</div>
                        <div className="text-[10px] text-slate-500">{c.mq} mq · {c.stato || "—"} · {c.fonte}</div>
                      </div>
                      <div className="text-right shrink-0">
                        <div className="text-xs font-bold text-[#0b2545]">€ {fmt(c.prezzo)}</div>
                        <div className="text-[10px] text-[#0d9488] font-semibold">{c.similarity}% simile</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {v.stato_note && (
              <div className="flex items-start gap-2 p-2.5 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-xs" data-testid="val-stato-note">
                <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <span>{v.stato_note}</span>
              </div>
            )}
            {v.zone_warning && (
              <div className="flex items-start gap-2 p-2.5 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-xs" data-testid="val-zone-warning">
                <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <span>{v.zone_warning}</span>
              </div>
            )}
            {v.photo_warning && (
              <div className="flex items-start gap-2 p-2.5 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-xs" data-testid="val-photo-warning">
                <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <span>{v.photo_warning}</span>
              </div>
            )}
            {onDownload && (
              <button type="button" onClick={onDownload}
                className="w-full mt-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-[#14B8A6] text-white text-sm font-semibold hover:bg-[#0d9488] shadow-sm transition"
                data-testid="val-download-pdf-card">
                <Download className="w-4 h-4" /> Scarica report PDF
              </button>
            )}
            <div className="mt-2 rounded-xl bg-[#0b2545] text-white p-4 space-y-2.5" data-testid="serenity-hook">
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-[#A9D6E5]">
                <ShieldCheck className="w-3.5 h-3.5" /> Vendita in serenità
              </div>
              <p className="text-xs leading-relaxed text-white/95" data-testid="serenity-hook-message">
                La stima è il primo passo, ma per vendere in totale sicurezza è fondamentale verificare la conformità urbanistico-catastale dell&apos;immobile. Solo affidandoti a Gruppo Vela puoi avere la certezza di una vendita senza sorprese e in totale serenità.
              </p>
              <p className="text-xs leading-relaxed text-[#A9D6E5]" data-testid="serenity-hook-positioning">
                Se vuoi risparmiare, puoi acquistare la valutazione e procedere in autonomia. Se vuoi vendere con la garanzia della serenità, il Sailing Team di Gruppo Vela è la tua unica rotta sicura. ⛵
              </p>
            </div>
          </>
        ) : (
          <div className="text-sm text-slate-600">{v.confidence_label || "Dati insufficienti per stima puntuale."}</div>
        )}
        <p className="text-[11px] leading-relaxed text-amber-800 bg-amber-50 border-l-2 border-amber-400 rounded-r px-2.5 py-2 mt-2" data-testid="valuation-disclaimer">⚠️ {v.disclaimer || "La valutazione è indicativa e si basa sui dati inseriti dall'utente. Se i dati non fossero confermati, il valore potrebbe cambiare sia in positivo che in negativo."}</p>
      </div>
    </div>
  );
}

